/**
 * FolderTree — 시험지 폴더 트리
 * (계약 정본: docs/exam-builder-v2/BE-20260826-audit-fixes-folder-tree-v2apis.md §2. 1세대는 BE-20260825-folders.md).
 *
 * 레거시 시험제작의 '폴더'는 라벨로 묶어 보여 주던 것이었지만 여기 폴더는 정식 소유 폴더다.
 * **2026-08-26 부터 폴더 안에 폴더가 들어간다** — 최상위를 1로 세어 깊이 최대 3, 순환 금지(계약 §2).
 * 그래서 이 화면이 반드시 말해야 하는 사실이 하나 있고, 그 문장의 뒤쪽 절반이 바뀌었다.
 * 앞쪽은 그대로다 — **폴더 삭제는 시험지 삭제가 아니다.**
 * 뒤쪽은 이제 갈래가 둘이다: 예전에는 "안에 있던 시험지는 무소속으로 살아남는다" 하나였는데,
 * 지금은 **하위 폴더와 시험지가 한 단계 위(상위 폴더)로 올라간다**. 무소속이 되는 것은
 * **최상위 폴더를 지울 때뿐**이다. 이 두 갈래를 확인 바가 갈라서 말하지 않으면, 하위 폴더를 지우는
 * 사람에게는 화면이 거짓말을 한다. 그래서 확인은 window.confirm 이 아니라 인라인 바다 —
 * 지울 폴더의 이름·그 안의 시험지 수·하위 폴더 수가 결정하는 순간 화면에 같이 남아 있어야 한다.
 *
 * **펼침 상태는 서버에 저장하지 않는다**(2026-09-09 오너 결정 · 설계 정본 §4 FS-01).
 * 전에는 접고 펼 때마다 PATCH {is_open} 이 나갔는데, 폴더가 공유되면 그 자리가 무너진다 —
 * 보기 상태는 사람마다 다른 것이라 여럿이 보는 값에 얹을 수 없다(남이 접은 것이 내 화면을 바꾼다).
 * 지금은 **이 브라우저 세션**이 쥔다(folderOpenState.js). 서버가 준 is_open 은 세션에 아무것도
 * 없을 때 **씨앗으로 한 번만** 읽고 그 뒤로는 보지 않으며, 서버에 쓰는 일은 없다.
 * 왕복이 사라졌으므로 낙관적 갱신·되돌리기·순번 관리도 함께 사라졌다.
 * 펼침이 여는 것은 그 폴더의 **자식 줄**이다. **접힌 폴더의 자손은 아예 그리지 않는다**
 * (숨기는 것이 아니라 만들지 않는다) — 무리 제목 줄(내 폴더 / 공유받은 폴더)도 같은 규칙이다.
 *
 * 끌어다 놓기가 두 종류다. **시험지**(PAPER_DND_MIME → movePapers)와 **폴더**(FOLDER_DND_MIME →
 * moveFolder)가 같은 행 위에 떨어질 수 있어, dragover 중에 읽을 수 있는 유일한 단서인
 * dataTransfer.types 로 갈래를 먼저 가른다. 폴더 쪽은 **화면에서 먼저 막는다** — 자기 자신·자기 자손
 * 위로는 놓을 수 없고, 끌고 있는 가지의 높이를 재어 옮긴 뒤 깊이가 3 을 넘으면 받지 않는다.
 * 서버(400)를 눌러 보고 알게 하지 않는다.
 *
 * 시험지 목록 자체는 이 컴포넌트가 읽지 않는다. 고른 폴더를 부모에게 올리면 부모가
 * listPapers({folderId, includeChildren}) 로 다시 읽는다 — 트리와 목록이 같은 데이터를 두 번 읽지 않게.
 * 「하위 폴더 포함」 스위치도 그 목록 머리줄에 있다(2026-08-28 오너 지시로 트리에서 옮겼다).
 *
 * **순서는 폴더명 오름차순 하나다**(오너 결정 2026-09-14 — 정렬 버튼 없이 늘 이 순서). 서버 tree 는 모양(부모·자식)만
 * 쓰고 순서는 화면이 다시 세운다(examPaperApi 의 compareFolders · sortFolderTree — 가나다, 숫자는 크기순, 같은 이름은
 * 만든 순). 원본 tree state 는 서버 순서 그대로 두고 **그리는 자리만** 정렬본을 쓴다 — 무리 나눔과 「옮길 곳」 목록.
 * [이동]·[복사]의 폴더 고르기(buildFolderTree)도 같은 비교를 써서 목록마다 순서가 갈리지 않는다.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Folder, FolderOpen, FolderPlus, FolderInput,
  Inbox, Layers, Pencil, Share2, Trash2, RefreshCw, AlertTriangle, MoreHorizontal, Check, User, Users,
} from 'lucide-react';
import { Button } from '../common';
import { PAPER_DND_MIME } from './PaperTable';
import { PaperScopeControls } from './PaperListControls';
import { useAuth } from '../../context/AuthContext';
import {
  splitTopLevel, isMineNode, buildGroupIndex, groupOf,
  authorLabel, sharedRowTitle, SHARED_LOCK, GROUP_LABEL,
  canPutPapersNode, shareStateOf, PAPER_LOCK, sharedOutRowTitle, SHARE_ALL_TEACHERS, shareCancelNote,
} from './folderOwnership';
import { readOpenState, writeOpenState } from './folderOpenState';
import {
  listFolders, createFolder, updateFolder, deleteFolder, moveFolder, movePapers, buildFolderTree, sortFolderTree,
  putFolderShares, describeApiError, MAX_FOLDER_DEPTH, UNFILED, AUTH_REDIRECT,
} from './examPaperApi';

/**
 * 깊이 들여쓰기 — **아이콘째로 민다.** 이름 앞에 공백(nbsp)만 넣던 때는 아이콘이 한 열에 서 있어
 * 깊이가 눈에 잡히지 않았다(오너 지적 2026-08-28). 파일 트리가 다 그렇게 하는 데는 이유가 있다.
 * 한 단 12px — 폴더 트리의 자식 들여쓰기(pl-3)와 같은 값이라 두 화면의 계단이 어긋나지 않는다.
 * 클래스를 문자열로 늘어놓는 이유: Tailwind 는 소스를 글자로 훑어 쓸 클래스를 정한다(계산식은 못 읽는다).
 */
const INDENT_BY_LEVEL = ['pl-2', 'pl-5', 'pl-8', 'pl-11'];
const indentOf = (level) => INDENT_BY_LEVEL[Math.min(Math.max(level, 0), INDENT_BY_LEVEL.length - 1)];

/**
 * 메뉴 안 안내를 「공유 폴더」만 굵게 세워 그리려고 문장을 세 조각으로 미리 가른다.
 * **문구 자체는 SHARED_LOCK 이 정한다**(FS-05) — 여기서 하는 일은 굵게 할 자리를 찾는 것뿐이라,
 * 문구를 고칠 때 이 파일의 태그를 함께 옮길 일이 없다. 굵게 할 말은 옮길 곳 후보가 쓰는
 * 사유(moveReason)와 같은 말이므로 그것을 축으로 삼는다.
 * 못 찾으면 굵게 세우지 않고 문장을 통째로 쓴다 — 문구가 바뀌어도 말이 잘리면 안 된다.
 */
const SHARED_MENU_PARTS = (() => {
  const i = SHARED_LOCK.menu.indexOf(SHARED_LOCK.moveReason);
  if (i < 0) return [SHARED_LOCK.menu, '', ''];
  return [
    SHARED_LOCK.menu.slice(0, i),
    SHARED_LOCK.moveReason,
    SHARED_LOCK.menu.slice(i + SHARED_LOCK.moveReason.length),
  ];
})();

/**
 * 범위 스위치의 라벨(FS-04) — 시험지 목록과 **같은 부품**(PaperScopeControls)을 쓰되 말만 폴더 것으로 바꾼다.
 * 한 화면에 이 스위치가 둘 서므로 양쪽 다 무엇의 범위인지 말한다(여기는 「내 폴더」, 목록은 「내 시험지」).
 */
const FOLDER_SCOPE_OPTIONS = [
  // 「전체」에 Users 를 쓰지 않는다 — 같은 트리에서 공유받은 줄의 손잡이가 Users 라,
  // 한 화면에서 같은 그림이 「거르지 않음」과 「남의 폴더」 두 뜻을 맡는다.
  // 바로 아래 [전체] 가상 줄이 이미 Layers 를 쓰고 있어 뜻이 맞는다.
  { mine: false, label: '전체', Icon: Layers },
  { mine: true, label: '내 폴더', Icon: User },
];

/**
 * 「내 폴더」만 볼 때의 한계 — 무리 나눔이 최상위 줄에만 걸리기 때문에 생기는 사실이라
 * 화면이 먼저 말해 준다(FS-04). 이 한 줄이 없으면 「하위 폴더가 사라졌다」로 읽힌다.
 */
const SCOPE_LIMIT_NOTE = '「내 폴더」는 내가 만든 최상위 폴더의 가지만 남깁니다 — 남의 폴더 안에 있는 내 하위 폴더는 「전체」에서 부모와 함께 보입니다.';

/**
 * 상자의 이름(aria-label) — **겹마다 다르다.** 화면에 안 보이고 스크린리더만 읽는 값이라,
 * 겹을 하나 더하면서 이 표를 빠뜨리면 「공유」 겹이 「폴더 메뉴」로 읽히며 조용히 거짓말을 한다.
 */
const MENU_VIEW_TITLE = { menu: '폴더 메뉴', move: '옮길 곳 고르기', share: '공유' };

/**
 * 공유 겹이 말하는 사실 — **계약이 보증하는 만큼만** 적는다.
 * 근거: BE-20260825-folders.md:42 「기본은 전체 — 매니저 공용」. 폴더는 만든 순간 관리자 모두에게 보인다.
 *
 * **2026-09-10 로 뒤쪽 절반이 바뀌었다.** 전에는 여기에 「교사(선생님)를 말하지 않는다 — 서버가
 * 교사에게 폴더 API 를 403 으로 막는 것은 실측일 뿐 계약이 아니라, 백엔드가 그 문을 여는 날 이
 * 문장이 거짓이 된다」고 적혀 있었다. **그 날이 왔다**(BE-20260910-reply-teacher-access-folder-sharing.md ·
 * BE-20260910-reply-share-all-teachers.md) — 교사는 이제 공유받은 폴더를 읽고, 폴더마다 「교사 전원」
 * 공유를 걸고 내릴 자리가 서버에 생겼다. 그래서 두 가지가 함께 바뀐다.
 *  - 교사 줄(`audienceTeachers`)은 **실제로 걸려 있을 때만** 선다. 계약이 여전히 보증하지 않는 것은
 *    「안 걸어도 보인다」쪽이라, 그 줄을 상시로 세우면 이번에는 반대 방향으로 거짓이 된다.
 *  - 「대상을 골라 공유하는 기능은 아직 없습니다」·「서버가 …기억하게 되면 이 자리에 붙습니다」 두 줄은
 *    **지웠다.** 저장할 자리가 생겼으니 거짓이고, 죽은 문구를 남기면 다음 사람이 현행으로 읽는다.
 *    그 자리에 [교사 전원에게 공유] / [공유 취소] 버튼이 선다.
 * 대상 고르기·검색칸·등급 선택은 그래도 두지 않는다 — 서버가 못 해서가 아니라 **오너가 정한 규칙**이다
 * (규칙 1·2: 상태는 둘, 대상은 교사 전원, 등급은 view 고정).
 */
const SHARE_VIEW = {
  // 손잡이 겹의 항목 이름 — 지금 상태를 그대로 읽는다. 누르면 여는 것은 같은 겹이라 ChevronRight 는
  // 두 상태가 함께 쓴다(「다른 폴더로 이동」과 같은 형식 — 겹이 하나 더 열린다는 표시).
  menuLabel: '공유',
  menuLabelOn: '공유 중',
  audienceHead: '볼 수 있는 사람',
  audience: '관리자 전원',
  audienceTeachers: '강사 전원',
  // 명단 오른쪽의 작은 꼬리표 — 전에는 이 두 사실이 명단 **아래 문장 두 개**였다(「폴더는 만들면
  // 관리자 모두에게 보입니다」·「여기에 더해 지금은 선생님 전원에게…」). 288px 폭에서 각각 두 줄로
  // 접혀 겹의 절반을 문장이 먹었고, 정작 누를 버튼과 글자 크기·색이 같아 어느 것이 손잡이인지
  // 구별되지 않았다(오너 지적 2026-09-10). 사실을 줄 안으로 옮기면 명단이 두 줄로 끝난다.
  audienceTag: '기본',
  teachersTag: '보기만',
};

/**
 * FolderMenu — 폴더 줄의 [⋯] 에 붙는 미니 모달(하위 폴더·이동·이름 변경·공유·삭제).
 *
 * 겹이 셋이다(view) — 'menu'(손잡이) · 'move'(옮길 곳 고르기) · 'share'(누가 볼 수 있는가).
 * 뒤의 둘은 **같은 상자 안에서** 연다(팝오버 위에 팝오버를 겹치면 바깥 클릭 규칙이 서로를 닫는다).
 *
 * 왜 줄 안에 늘어놓지 않는가 — 전에는 이 셋이 **펼친 폴더 안에** 상시로 놓여 있었다.
 * 그래서 「펼치기」가 두 가지를 한꺼번에 뜻했다: 자식 보기와 손잡이 꺼내기. 자식만 보고 싶어 펼쳐도
 * 버튼 세 개가 따라 나와 줄이 시끄러웠고, 손잡이만 쓰려 해도 자식이 함께 펼쳐졌다.
 * 이제 **펼치기는 자식 전용**이고 손잡이는 이 메뉴가 쥔다.
 *
 * 자리 잡는 법·닫는 조건은 형제 미니 모달(PaperTable 의 MovePopover)과 **같은 규칙**을 쓴다 —
 * 트리가 세로로 스크롤하는 상자 안에 있어(overflow-y-auto) 그 안에 절대 배치하면 아래쪽 줄에서 잘린다.
 * 그래서 body 로 포털해 fixed 로 띄우고, 스크롤·리사이즈에는 따라다니지 않고 닫는다.
 */
export function FolderMenu({
  name, rect, countText, readOnly, canAddChild, moveOptions, currentParentId,
  /* 공유는 **불리언 셋**으로 받는다 — 한 값에 네 상태를 싣지 않는다.
     `canShare`   [공유] 항목을 그릴지 — 내 최상위 폴더이고 서버가 이 기능을 아는가
     `shareOn`    지금 「교사 전원」이 걸려 있는가
     `shareSubOnly` **하위 폴더라서** 못 하는가 — 항목 대신 이유를 세우는 자리다.
       이 셋째 값이 필요한 이유: canShare 하나로는 「하위 폴더라 없다」와 「구 서버라 없다」가
       같은 거짓이 되어 겹 안에서 구별할 수 없다. 그런데 사용자에게는 **정반대**다 — 앞은
       「위 폴더에서 하면 된다」고 말해 줘야 하고, 뒤는 아무 말도 하지 않아야 한다(오너 규칙 6:
       서버가 이 기능을 모르면 공유를 아예 보이지 않게). 구별하지 않으면 오너가 실제로 물은
       「일부러 막아 놨나」로 끝난다(2026-09-10).
     판정 자체는 호출부가 folderOwnership 의 shareStateOf 로 만든다 — 여기서 undefined/null 을
     다시 가르면 그 판정이 두 벌이 된다. */
  canShare = false, shareOn = false, shareSubOnly = false,
  onAddChild, onMove, onRename, onDelete, onShare, onUnshare, onClose,
}) {
  const boxRef = useRef(null);
  // 'menu' | 'move' | 'share' — 뒤의 둘을 **같은 상자 안에서** 연다. 팝오버 위에 팝오버를 겹치면
  // 바깥 클릭으로 닫는 규칙이 서로를 닫아 버린다.
  const [view, setView] = useState('menu');
  // 재기 전에는 화면 밖에 둔다 — 왼쪽 위에 한 번 그렸다가 제자리로 뛰는 깜빡임을 없앤다
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (el == null) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    // 버튼의 오른쪽 끝에 메뉴의 오른쪽을 맞춘다 — 트리가 좁아 왼쪽 정렬하면 화면 밖으로 넘친다
    const left = Math.max(8, Math.min(rect.right - w, window.innerWidth - w - 8));
    const below = rect.bottom + 6;
    // 아래가 모자라면 버튼 위로 뒤집는다. 뒤집은 뒤에도 위아래를 한 번 더 조인다(창이 아주 낮을 때)
    const want = below + h <= window.innerHeight - 8 ? below : rect.top - h - 6;
    const top = Math.max(8, Math.min(want, window.innerHeight - h - 8));
    setPos({ left, top });
  }, [rect, view]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true); // true — 트리 안쪽 스크롤도 잡는다
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const rowCls = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors';
  /** 줄 오른쪽의 작은 꼬리표 — 값이 아니라 **그 값의 성격**을 말한다. 트리 줄의 개수 배지와 같은 얼굴이다. */
  const tagCls = 'shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500';

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      aria-modal="false"
      aria-label={`${name} ${MENU_VIEW_TITLE[view] || MENU_VIEW_TITLE.menu}`}
      /* 폭은 겹이 정한다 — 'menu'·'move' 는 짧은 항목뿐이라 224px 로 맞지만, 'share' 에는 문장이 있어
         그 폭에서 서너 줄로 접힌다. 두 값을 **글자로** 남긴다(Tailwind 는 소스를 훑어 쓸 클래스를 정한다). */
      className={`fixed rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl ${view === 'share' ? 'w-72' : 'w-56'}`}
      style={{
        zIndex: 'var(--z-dropdown)',
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {view === 'menu' && (
        <>
          {/* 머리 — 어느 폴더의 메뉴인지, 그 안에 무엇이 몇 개인지.
              이 두 줄이 전에 펼친 자리에 있던 「시험지 N개」 캡션을 대신한다. */}
          <p className="truncate px-2 pt-0.5 text-xs font-bold text-gray-800" title={name}>{name}</p>
          <p className="px-2 pb-1 text-[10px] font-medium text-gray-400">{countText}</p>
          {/* readOnly 를 함께 본다 — 「고칠 수 없는 폴더」라는 사실은 이 상자 안에서 한 규칙으로 판정한다.
              호출부도 menuFor.mine 으로 막고 있지만, 두 곳이 따로 판정하면 한쪽만 고쳐지는 날
              「고칠 수 없습니다」 안내 옆에 [하위 폴더] 버튼이 나란히 서게 된다. */}
          {canAddChild && !readOnly && (
            <button type="button" onClick={onAddChild} className={`${rowCls} text-gray-700 hover:bg-gray-50`}>
              <FolderPlus aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
              <span className="min-w-0 flex-1 truncate">하위 폴더</span>
            </button>
          )}
          {/* 옮기기 — 지금까지 드래그로만 되던 일이다. 좁은 트리에서 깊은 자리로 끄는 것은
              손이 많이 가고, 접힌 폴더 안으로는 끌 수도 없다. 그래서 고르는 길을 함께 둔다. */}
          {!readOnly && (
            <>
              <button type="button" onClick={() => setView('move')} className={`${rowCls} text-gray-700 hover:bg-gray-50`}>
                <FolderInput aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1 truncate">다른 폴더로 이동</span>
                <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-gray-300" />
              </button>
              <button type="button" onClick={onRename} className={`${rowCls} text-gray-700 hover:bg-gray-50`}>
                <Pencil aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1 truncate">이름 변경</span>
              </button>
              {/* 공유 — 「이름 변경」 다음, 「삭제」 앞. 위 셋과 같이 **폴더 자체를 다루는** 일이고,
                  삭제만 성격이 달라 맨 아래에 떨어져 있다.
                  아이콘에 Users 를 쓰지 않는다 — 이 파일에서 Users 는 이미 「공유받은 폴더(남의 것)」를
                  뜻한다(줄 손잡이·아래 안내). 같은 그림이 「남의 폴더」와 「공유하기」 두 뜻을 맡으면 안 된다.
                  ⚠ 줄의 「공유 중」 표시는 **오너 지시로 Users 다** — 이 판정과 어긋나며, 그 사실과
                  자리로 가른 이유는 그 자리(renderNode)의 주석에 적어 두었다. 여기는 그대로 Share2 다.
                  ChevronRight 는 「다른 폴더로 이동」과 같은 형식 — 겹이 하나 더 열린다는 표시다.
                  **canShare 가 거짓이면 항목째 없다.** 겹으로 들어가는 길이 여기 하나뿐이라, 이 조건
                  하나로 「구 서버에서는 공유를 아예 보이지 않는다」(오너 규칙 6)가 성립한다 —
                  겹 안에서 다시 거르면 들어갈 수는 있는 빈 방이 생긴다. */}
              {canShare && (
                <button type="button" onClick={() => setView('share')} className={`${rowCls} text-gray-700 hover:bg-gray-50`}>
                  <Share2 aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
                  <span className="min-w-0 flex-1 truncate">{shareOn ? SHARE_VIEW.menuLabelOn : SHARE_VIEW.menuLabel}</span>
                  <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-gray-300" />
                </button>
              )}
              {/* 하위 폴더 — 항목을 그냥 없애면 「일부러 막아 놨나」로 끝난다(오너가 실제로 물었다).
                  계약이 최상위만 받으므로(400 not_root) 항목을 세울 수는 없지만, **어디서 하면
                  되는지**는 말할 수 있다. 눌리지 않는 회색 버튼으로 두지 않는 이유는 배정 삭제와 같다 —
                  풀 수 없는 조건을 잠긴 손잡이로 보이면 사용자가 계속 눌러 본다. */}
              {shareSubOnly && (
                <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] leading-relaxed text-gray-500">
                  <Share2 aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-gray-400" />
                  <span>{SHARE_ALL_TEACHERS.rootOnly}</span>
                </p>
              )}
              <button type="button" onClick={onDelete} className={`${rowCls} text-red-700 hover:bg-red-50`}>
                <Trash2 aria-hidden="true" className="size-3.5 shrink-0 text-red-400" />
                <span className="min-w-0 flex-1 truncate">삭제</span>
              </button>
            </>
          )}
          {/* 손잡이를 통째로 감추면 「왜 아무것도 없지」로 끝난다 — 이유를 이 자리에 적는다.
              볼 수 있는 것(고르기·펼치기·시험지 목록)은 그대로라는 것도 함께 말한다. */}
          {readOnly && (
            <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] leading-relaxed text-gray-500">
              <Users aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-gray-400" />
              <span>
                {SHARED_MENU_PARTS[0]}
                {SHARED_MENU_PARTS[1] !== '' ? <b>{SHARED_MENU_PARTS[1]}</b> : null}
                {SHARED_MENU_PARTS[2]}
              </span>
            </p>
          )}
        </>
      )}

      {view === 'share' && (
        <>
          {/* 돌아가는 길 — 'move' 겹과 **같은 형식**이다. 겹이 둘로 늘었다고 나가는 법이 둘이 되면 안 된다. */}
          <button
            type="button"
            onClick={() => setView('menu')}
            className="flex w-full items-center gap-1 rounded-lg px-1.5 py-1 text-left text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-50"
          >
            <ChevronLeft aria-hidden="true" className="size-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{name}</span>
          </button>

          {/* (가) 지금 볼 수 있는 사람 — **고르는 목록이 아니라 사실 두 줄**이라 버튼이 아니다.
              여기서는 Users 가 「여러 사람」이라는 본뜻이라 맞다(줄 손잡이의 「남의 폴더」 뜻과 겹치지 않는다).
              계약(BE-20260825-folders.md:42)이 보증하는 것은 「매니저 공용」까지고, 교사 줄은
              **실제로 걸려 있을 때만** 선다 — 이 묶음은 「지금」을 말하는 자리라 걸지도 않은 공유를
              세워 두면 화면이 없는 사실을 말한다.

              오른쪽 꼬리표(기본 / 보기만)는 개수 배지와 **같은 얼굴**이다(회색 알약 10px) —
              이 상자에서 「값이 아니라 그 값의 성격」을 말하는 자리가 이미 그 모양이다. */}
          <p className="px-2 py-1 text-[10px] font-medium text-gray-400">{SHARE_VIEW.audienceHead}</p>
          <p className={`${rowCls} text-gray-700`}>
            <Users aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
            <span className="min-w-0 flex-1 truncate">{SHARE_VIEW.audience}</span>
            <span className={tagCls}>{SHARE_VIEW.audienceTag}</span>
          </p>
          {shareOn && (
            <p className={`${rowCls} text-gray-700`}>
              <Users aria-hidden="true" className="size-3.5 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate font-medium">{SHARE_VIEW.audienceTeachers}</span>
              <span className={tagCls}>{SHARE_VIEW.teachersTag}</span>
            </p>
          )}

          {/* (나) 손잡이 — **상태가 둘뿐이라 버튼도 하나뿐이다**(오너 규칙 1).
              대상을 고르는 목록·검색칸·등급 선택은 두지 않는다(규칙 2: 교사 전원 · view 고정).
              [공유]는 바로 나가고, **취소만** 줄 아래 확인 바를 거친다(규칙 3).

              ⚠ **면을 채운 버튼으로 세운다**(2026-09-10 오너 지적 — 「공유버튼도 또렷히 안보이고」).
              전에는 위 명단과 똑같은 rowCls 였다: 같은 글자 크기·같은 회색 아이콘·투명 배경이라
              **읽는 줄과 누르는 줄이 한 얼굴**이었고 차이가 hover 하나뿐이었다. 이 겹의 행동은
              이것 하나뿐이니 눈에 먼저 들어와야 한다 — 채운 면 · 가운데 정렬 · 굵은 글자 · 넉넉한 위아래.
              색: 공유는 프로젝트 색, 취소는 amber 테두리다. **빨강은 이 파일에서 [삭제] 전용**이라
              되돌릴 수 있는 일에 쓰지 않는다.
              '../common' 의 Button 을 쓰지 않는 이유는 그대로다 — 배럴을 처음 끌어오면 이 부품만
              세워 보는 시험(FolderMenu.test.jsx)의 mock 경계까지 함께 움직인다. */}
          <div className="mt-2 border-t border-gray-100 px-1 pb-0.5 pt-2">
            {shareOn ? (
              <button
                type="button"
                onClick={onUnshare}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
              >
                <Share2 aria-hidden="true" className="size-3.5 shrink-0" />
                {SHARE_ALL_TEACHERS.cancel}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onShare}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary p-2 text-xs font-medium text-white transition-colors hover:bg-primary-hover"
                >
                  <Share2 aria-hidden="true" className="size-3.5 shrink-0" />
                  {SHARE_ALL_TEACHERS.action}
                </button>
                {/* 두 사실을 **한 문단**으로 잇는다 — 등급이 보기 고정이라는 것과, 이 공유가
                    하위까지 번진다는 것. 문장을 따로 세우면 288px 에서 각각 두 줄이 되어
                    버튼보다 글이 커진다. */}
                <p className="px-1 pt-1.5 text-[11px] leading-relaxed text-gray-500">
                  {SHARE_ALL_TEACHERS.grade} · {SHARE_ALL_TEACHERS.scope}
                </p>
              </>
            )}
          </div>
        </>
      )}

      {view === 'move' && (
        <>
          <button
            type="button"
            onClick={() => setView('menu')}
            className="flex w-full items-center gap-1 rounded-lg px-1.5 py-1 text-left text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-50"
          >
            <ChevronLeft aria-hidden="true" className="size-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{name}</span>
          </button>
          <p className="px-2 pb-1 pt-0.5 text-[10px] font-medium text-gray-400">옮길 곳</p>
          <div className="max-h-64 overflow-y-auto">
            {moveOptions.map((o) => {
              const here = sameSel(currentParentId, o.id);
              const Icon = o.id == null ? Inbox : Folder;
              return (
                <button
                  key={o.id == null ? 'root' : o.id}
                  type="button"
                  // 지금 있는 자리를 다시 고르면 아무 일도 안 일어나야 한다 — 빈 요청은 서버에 판만 쌓는다
                  onClick={() => (here ? onClose() : onMove(o.id))}
                  disabled={o.blocked}
                  title={o.blocked
                    ? (o.reason === SHARED_LOCK.moveReason
                      ? SHARED_LOCK.moveTitle
                      : '이 폴더로 옮기면 깊이가 3을 넘습니다')
                    : undefined}
                  className={`${rowCls} ${indentOf(o.level)} disabled:cursor-not-allowed disabled:opacity-40 ${
                    here ? 'bg-primary/10 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
                  <span className="min-w-0 flex-1 truncate">{o.name}</span>
                  {here && <Check aria-hidden="true" className="size-3.5 shrink-0 text-primary" />}
                  {!here && o.blocked && <span className="shrink-0 text-[9px] text-gray-400">{o.reason}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

/** 목록 항목의 식별자 — 계약은 folder_id(정수)를 경로·필터에 쓴다(folder_uuid 는 표시용) */
export const folderIdOf = (f) => f?.folder_id ?? f?.id ?? null;

/**
 * 폴더 드래그 페이로드 MIME — 시험지 드래그(PAPER_DND_MIME)와 **같은 행 위에** 떨어지므로
 * 갈래를 이름으로 갈라야 한다. dragover 중에는 보안상 getData 가 막히고 types 만 읽을 수 있어,
 * "이게 폴더 드래그인가"를 판정할 수단이 전용 MIME 말고는 없다.
 */
export const FOLDER_DND_MIME = 'application/x-ky-folder';

/** 최상위로 올리는 드롭 자리의 표식 — 폴더 id 와 섞이지 않게 문자열로 둔다 */
const ROOT_DROP = '__root__';

const folderLabel = (f) => String(f?.name ?? '').trim() || '(이름 없음)';

const numOrNull = (v) => {
  // Number(null) 은 0 이다 — 걸러 내지 않으면 '값이 없다'가 '0개'로 둔갑해 배지·삭제 확인이 거짓말을 한다
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** paper_count(직접 소속)는 계약이 주기로 한 값이지만, 없을 때 0 으로 단정하면 삭제 확인이 거짓말을 한다 */
const paperCountOf = (f) => numOrNull(f?.paper_count);
/** total_paper_count = 자손 폴더까지 포함한 수(계약 §2). 직접 소속과 **다를 때만** 화면에 둘 다 그린다 */
const totalCountOf = (f) => numOrNull(f?.total_paper_count);
/** child_count 가 비어 오면 트리가 이미 갖고 있는 children 으로 센다 */
const childCountOf = (f) => numOrNull(f?.child_count);

const childrenOf = (f) => (Array.isArray(f?.children) ? f.children : []);

/** 깊이는 서버가 준 값(1~3)을 쓰고, 없으면 그리는 중첩 단계로 대신한다(최상위=1) */
const depthOf = (f, level) => {
  const d = numOrNull(f?.depth);
  return d != null && d >= 1 ? d : level + 1;
};

// 선택값은 정수 | 'none'(무소속) | null(전체) 세 갈래다. null 과 'none' 은 뜻이 다르므로
// 느슨한 비교로 뭉개지 않는다 — 'none' 을 전체로 읽으면 무소속 필터가 통째로 사라진다.
const sameSel = (a, b) => (a == null && b == null) || (a != null && b != null && String(a) === String(b));

/**
 * 중첩 트리에서 폴더 하나만 바꿔 새 트리를 만든다 — 접고 펴기·이름 바꾸기를 낙관적으로 그릴 때 쓴다.
 * 평면 배열이었을 때의 map 한 줄을 대신하는 자리라, 여기서 재귀를 멈추면 자식 줄의 값이 낡는다.
 */
const patchNode = (nodes, id, patch) => (Array.isArray(nodes) ? nodes : []).map((n) => (
  sameSel(folderIdOf(n), id)
    ? { ...n, ...patch }
    : { ...n, children: patchNode(n.children, id, patch) }
));

function FolderTree({
  selectedFolderId = null,
  onSelectFolder,
  onChanged = null,
  reloadKey = 0,
  // 부모가 [이동]·[복사] 대화에서 FolderPickerInline 에 넘길 목록. 트리가 이미 읽은 것을
  // 그대로 올려 준다 — 같은 목록을 부모가 또 읽으면 두 화면의 폴더 수가 갈릴 수 있다.
  // 계층이 생겨도 여기 올리는 것은 **평면 items** 다(피커가 자기 안에서 다시 계층으로 접는다).
  onFoldersLoaded = null,
}) {
  // userId 는 세션 키의 계정 부분이다 — 같은 탭에서 계정을 바꿔 로그인해도 남의 화면 상태를
  // 물려받지 않게. 토큰을 파싱할 필요가 없다(AuthContext 가 이미 값으로 준다).
  const { getAccessToken, userId } = useAuth();
  // 평면 items — 빈 상태 판정과 부모에게 올리는 몫.
  // ⚠ 이 배열의 is_open 은 **서버가 실어 준 값 그대로 고인 죽은 값**이다(FS-01 이후 아무도 쓰지 않는다).
  //    펼침의 정본은 아래 openState 하나뿐이니, 여기서 펼침을 읽지 마라.
  const [folders, setFolders] = useState([]);
  const [tree, setTree] = useState([]);       // 중첩 tree — 화면이 그리는 몫(계약 §2)
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  // 실패했을 때만 쓰는 자리. 성공 문구는 두지 않는다(오너 지시 2026-08-28) —
  // 폴더를 옮기고 지우고 이름을 바꾼 결과는 **트리가 곧바로 그렇게 그려져서** 이미 보인다.
  // 보이는 것을 글로 한 번 더 말하면 줄만 늘고, 정작 실패했을 때의 빨간 글자가 묻힌다.
  const [toast, setToast] = useState(null); // { text } — 오류 문구 하나
  const [creating, setCreating] = useState(null); // null | { parentId: 정수|null } — null 이면 최상위에 만든다
  const [newName, setNewName] = useState('');
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [confirmId, setConfirmId] = useState(null); // 삭제 확인 바 — 한 번에 하나만
  // 공유 취소 확인 바 — 삭제와 **다른 상태**이되 규칙은 같다(한 번에 하나만, 서로도 겹치지 않는다).
  // 한 값에 담지 않는 이유: 두 확인은 지우는 것도 되돌릴 수 있는 정도도 달라 문장·버튼 색이 갈린다.
  const [unshareId, setUnshareId] = useState(null);
  // [⋯] 미니 모달 — { f, rect }. 한 번에 하나만 열린다(rect 는 연 버튼의 화면 좌표)
  const [menuFor, setMenuFor] = useState(null);
  const [busy, setBusy] = useState(null); // 'create' | `rename:${id}` | `delete:${id}` | `share:${id}`(걸기·내리기 공용)
  /**
   * 펼침 상태 — 서버가 아니라 **이 브라우저 세션**이 쥔다(FS-01).
   * folders: 펼친 폴더 id 집합(문자열) · groups: 무리 펼침({mine, shared}) ·
   * seeded: 서버 is_open 을 씨앗으로 옮겨 심었는가.
   *
   * 읽기는 **첫 렌더에 한 번만** 한다(지연 초기화). effect 로 읽고 cleanup 에서 되쓰는 형태를
   * 쓰지 않는 이유는 StrictMode 다 — 마운트 → cleanup → 마운트를 돌리므로 첫 cleanup 이
   * 초기값을 실제 값 위에 덮어쓴다. 쓰기는 사용자가 접고 펴는 **그 순간**에 직접 한다.
   *
   * tree 안에 얹지 않는 이유: load() 가 목록을 다시 읽는 순간(아래 setTree) 세션 값이 서버 값으로
   * 통째로 덮인다. 그 재조회는 폴더와 무관한 조작(시험지 저장·삭제·채점)에서도 부모를 통해
   * 돌아오므로, 같은 자리에 두면 재조회 경로 전부가 펼침을 되돌리는 자리가 된다.
   */
  const [openState, setOpenState] = useState(() => {
    const restored = readOpenState(userId);
    return {
      folders: restored ? restored.folders : new Set(),
      groups: restored ? restored.groups : { mine: true, shared: true },
      // 저장된 것이 없을 때(null)만 씨앗을 심는다. 「전부 접음(빈 집합)」과 구별되지 않으면
      // 사용자가 전부 접은 순간 씨앗이 다시 심겨 다음 렌더에 도로 펼쳐진다.
      seeded: restored != null,
    };
  });
  // 범위 스위치(FS-04) — 'all' | 'mine'. **기억하지 않는다**: 무엇을 보는지 정하는 조건이라
  // 화면을 열 때마다 같은 자리(전체)에서 시작하는 편이 예측 가능하다. 펼침(세션 저장)과는 성격이 다르다.
  const [scope, setScope] = useState('all');

  const seqRef = useRef(0);
  const aliveRef = useRef(true);
  // 세션에 저장할 때 가지치기의 기준이 되는 「지금 목록에 있는 id」 — **성공한 load 만** 채운다.
  // null 이면 가지치기하지 않는다. 조회에 실패하면 목록이 비는데(catch 의 setTree([])), 그 빈
  // 목록으로 가지치기하면 네트워크가 한 번 끊긴 것으로 펼쳐 둔 것이 전부 지워진다.
  const liveIdsRef = useRef(null);
  // 콜백을 ref 로 받아 둔다 — load 의 의존성에 넣으면 부모가 함수를 새로 만들 때마다 다시 읽는다
  const loadedCbRef = useRef(onFoldersLoaded);
  useEffect(() => { loadedCbRef.current = onFoldersLoaded; });

  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다 — 마운트마다 세우지 않으면 dev 에서 false 로 굳는다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fail = (text) => setToast({ text });

  const load = useCallback(async () => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      // 조회는 한 번이다. 전에는 `?mine=1` 목록을 한 번 더 불러 두 목록을 대조했는데,
      // 이제 항목·tree 노드가 각자 `is_mine` 을 싣는다(BE-20260829-reply-fe-0828.md §3 3-1) —
      // 소유는 아래 isMine 이 노드에서 바로 읽는다.
      const d = await listFolders({}, getAccessToken);
      if (!aliveRef.current || seq !== seqRef.current) return null;
      const items = Array.isArray(d.items) ? d.items : [];
      // tree 는 서버가 준 **모양**을 그대로 쥔다 — 순서는 그릴 때 폴더명 오름차순으로 다시 세운다(아래 shownTree).
      // 봉투가 낡아 비어 오면 examPaperApi 가 이미 평면에서 만들어 채워 주지만, 여기서도 마지막으로 한 번 더 받친다.
      const nodes = Array.isArray(d.tree) && d.tree.length > 0 ? d.tree : buildFolderTree(items);
      setFolders(items);
      setTree(nodes);
      setUnfiledCount(Number(d.unfiledCount) || 0);
      setLoaded(true);
      // 여기부터는 **관문(seq 대조)을 통과한 응답**만 온다 — StrictMode 가 버리는 첫 응답이
      // 씨앗을 심거나 가지치기 기준을 바꾸면 안 되므로 이 자리 말고 다른 데 두지 않는다.
      const live = new Set();
      const seed = new Set();
      const walkOpen = (list) => (Array.isArray(list) ? list : []).forEach((n) => {
        const nid = folderIdOf(n);
        if (nid != null) {
          live.add(String(nid));
          if (n.is_open === true) seed.add(String(nid));
        }
        walkOpen(childrenOf(n));
      });
      walkOpen(nodes);
      liveIdsRef.current = live;
      // 씨앗 — 세션에 저장된 것이 없을 때만 서버 is_open 을 한 번 옮겨 심는다. 값이 이미 있고
      // 지금 화면과 이어지기 때문이다. 그 뒤로는 내 세션이 이기고 서버 is_open 은 읽지 않는다.
      // 여기서 세션에 쓰지는 않는다 — 사용자가 아무것도 건드리지 않았는데 남길 값이 아니고,
      // 다시 마운트되면 같은 서버 값으로 같은 씨앗이 심긴다(서버 값은 이제 바뀌지 않는다).
      setOpenState((prev) => (prev.seeded ? prev : { ...prev, folders: seed, seeded: true }));
      if (loadedCbRef.current) loadedCbRef.current(items);
      return { items, tree: nodes };
    } catch (e) {
      if (!aliveRef.current || seq !== seqRef.current) return null;
      setError(e.message || '폴더를 불러오지 못했습니다.');
      setFolders([]);
      setTree([]);
      return null;
    } finally {
      if (aliveRef.current && seq === seqRef.current) setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { load(); }, [load, reloadKey]);

  /**
   * 트리 색인 — 그리는 것 말고 **판정에 쓰는** 값들이다.
   * level(0부터) · parentId · height(자기를 1로 센 서브트리 높이).
   * height 가 필요한 이유는 드래그다: 두 단짜리 가지를 깊이 2 폴더에 넣으면 3 을 넘는다(계약 §2).
   */
  const treeIndex = useMemo(() => {
    const byId = new Map();
    const walk = (nodes, level, parentId) => {
      let tallest = 0;
      (Array.isArray(nodes) ? nodes : []).forEach((n) => {
        const id = folderIdOf(n);
        const below = walk(childrenOf(n), level + 1, id);
        if (id != null) byId.set(String(id), { node: n, level, parentId, height: below + 1 });
        tallest = Math.max(tallest, below + 1);
      });
      return tallest;
    };
    walk(tree, 0, null);
    return byId;
  }, [tree]);

  const entryOf = (id) => (id == null ? null : treeIndex.get(String(id)) || null);

  /**
   * 이 줄을 고칠 수 있는가 — **그 노드 자신의 소유**다(무리가 아니다).
   * 남의 최상위 폴더 안에 있는 내 하위 폴더는 「공유받은 폴더」 무리에 그려지지만, 그 줄은
   * 내가 만든 것이라 이름 변경·삭제·드래그가 열려 있어야 한다. 그래서 여기는 groupOf 가 아니라
   * isMineNode 를 쓴다.
   * 규칙 자체(`is_mine !== false` — **모르면 잠그지 않는다**)는 folderOwnership 이 정한다.
   * 전에는 같은 규칙을 이 자리에 한 번 더 적어 두었는데, 한쪽만 고치는 날 무리 나눔과 줄 잠금이
   * 서로 다른 답을 내면서도 **빌드는 통과한다.**
   */
  const isMine = (id) => isMineNode(entryOf(id)?.node);

  // ── 선택 올리기 ────────────────────────────────────────────────────────
  //
  // 고른 것만 올린다. **「하위 폴더 포함」은 이 트리가 쥐지 않는다** — 오너 지시(2026-08-28)로 그
  // 스위치를 시험지 목록 머리줄(PaperListPanel)로 옮겼고, 상태는 부모(pages/ExamPapers.js)가 쥔다.
  // 여기 두면 「무엇을 보는가」를 정하는 스위치가 트리 밑에 숨어, 정작 결과가 바뀌는 목록에서 멀다.
  const select = (v) => { if (typeof onSelectFolder === 'function') onSelectFolder(v); };
  const selectFolder = (id) => select(id);

  const notifyChanged = () => { if (typeof onChanged === 'function') onChanged(); };

  /** 상태와 세션을 **한 자리에서** 함께 바꾼다 — 둘로 나누면 화면과 저장된 값이 갈라진다. */
  const commitOpenState = (value) => {
    setOpenState(value);
    writeOpenState(userId, value, liveIdsRef.current);
  };

  /**
   * 펼침/접힘 — 이 세션에만 남는다(FS-01). 서버 왕복이 없어 기다릴 것도 되돌릴 것도 없다.
   *
   * **다음 값을 부르는 쪽이 정해 넘긴다.** 노드에서 파생하면 [⋯] 메뉴가 얼려 둔 낡은 스냅샷이
   * 지금 화면을 뒤집고, 렌더의 펼침 판정과 출처가 갈려 「골랐는데 안 펼쳐진다」가 난다.
   * id 를 거르는 이유는 이 트리가 id 없는 노드를 상정하기 때문이다(key={id ?? name}) —
   * 'null' 이 세션에 들어가면 서로 다른 무명 폴더가 한 칸을 나눠 쓴다.
   */
  const toggleOpen = (id, next) => {
    if (id == null) return;
    const key = String(id);
    // 이름을 nextFolders 로 두는 이유: 이 컴포넌트에는 같은 이름의 평면 목록 state(folders)가 있다.
    const nextFolders = new Set(openState.folders);
    if (next) nextFolders.add(key);
    else nextFolders.delete(key);
    commitOpenState({ ...openState, folders: nextFolders });
  };

  /** parentId 가 null 이면 최상위, 정수면 그 폴더 안(계약 §2 POST /folders {parent_id}) */
  const startCreate = (parentId = null) => {
    setConfirmId(null);
    setUnshareId(null);
    setRenameId(null);
    setNewName('');
    setCreating({ parentId });
  };
  const cancelCreate = () => { setCreating(null); setNewName(''); };

  const submitCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (name === '' || busy != null || creating == null) return;
    const parentId = creating.parentId ?? null;
    setBusy('create');
    try {
      const d = await createFolder({ name, parentId }, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      cancelCreate();
      await load();
      notifyChanged();
    } catch (err) {
      if (aliveRef.current) fail(describeApiError(err, '폴더를 만들지 못했습니다.'));
    } finally {
      if (aliveRef.current) setBusy(null);
    }
  };

  const startRename = (f) => {
    setConfirmId(null);
    setUnshareId(null);
    setCreating(null);
    setRenameId(folderIdOf(f));
    setRenameName(String(f?.name ?? ''));
  };

  const submitRename = async (e, f) => {
    e.preventDefault();
    const id = folderIdOf(f);
    const name = renameName.trim();
    if (id == null || name === '' || busy != null) return;
    if (name === String(f?.name ?? '')) { setRenameId(null); return; }
    setBusy(`rename:${id}`);
    try {
      const d = await updateFolder(id, { name }, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      const saved = d?.name || name;
      setTree((prev) => patchNode(prev, id, { name: saved }));
      setFolders((prev) => prev.map((x) => (sameSel(folderIdOf(x), id) ? { ...x, name: saved } : x)));
      setRenameId(null);
      // 시험지 요약이 folder_name 을 싣고 온다 — 이름만 바뀌어도 목록의 표기가 낡는다
      notifyChanged();
    } catch (err) {
      if (aliveRef.current) fail(err.message || '폴더 이름을 바꾸지 못했습니다.');
    } finally {
      if (aliveRef.current) setBusy(null);
    }
  };

  const runDelete = async (f) => {
    const id = folderIdOf(f);
    if (id == null || busy != null) return;
    setBusy(`delete:${id}`);
    try {
      const d = await deleteFolder(id, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      // 응답 {folder_id, detached_papers, lifted_folders, parent_id} — 계약 §2.
      // 문구로 말하지는 않지만(성공 안내를 두지 않는다) **어디로 데려갈지**는 이 값으로 정한다.
      const detached = numOrNull(d?.detached_papers);
      const upTo = d?.parent_id ?? f?.parent_id ?? null; // 올라간 곳. null 이면 최상위(시험지는 무소속)
      setConfirmId(null);
      // 지워진 폴더를 보고 있었다면 그 자리에 남겨 둘 수 없다. 안에 있던 것들이 간 곳으로 데려간다 —
      // 어디로 갔는지 말로만 알리고 끝내지 않는다. 상위 폴더가 있으면 그리로, 없으면 무소속으로.
      if (sameSel(selectedFolderId, id)) {
        if (upTo != null) selectFolder(upTo);
        else select(detached != null && detached > 0 ? UNFILED : null);
      }
      await load();
      notifyChanged();
    } catch (err) {
      if (aliveRef.current) fail(describeApiError(err, '폴더를 삭제하지 못했습니다.'));
    } finally {
      if (aliveRef.current) setBusy(null);
    }
  };

  /**
   * 「교사 전원」 공유를 걸고 내린다 — PUT /folders/{id}/shares (계약 §2-3). 골격은 runDelete 와 같다.
   *
   * **한 함수가 두 방향을 맡는다.** 왕복도, 다시 읽는 자리도, 실패 처리도 같고 다른 것은 실어 보내는
   * 값 하나(`'view'` / `null`)뿐이다. 갈라 두면 성공 뒤 화면을 맞추는 자리가 두 벌이 되어,
   * 한쪽만 고쳐지는 날 「걸면 표시가 바뀌는데 내리면 안 바뀐다」가 난다.
   * `items` 키는 **보내지 않는다** — 계약이 「키가 없으면 그대로」라, API 로 직접 걸린 개별 공유가
   * 있으면 그것은 남는다. 이 화면에는 개별 공유를 만드는 길이 없어(오너 규칙 1) 확인 바 문장
   * (shareCancelNote)은 그 이야기를 하지 않는다 — 그 틈을 막아야 하면 취소에 `items: []` 를 함께 싣는다.
   *
   * ⚠ **notifyChanged() 를 부르지 않는다.** 공유는 시험지 목록의 모집단을 바꾸지 않는다 —
   *    부르면 onChanged → refreshKey → reloadKey 로 load() 가 한 번 더 도는 왕복만 는다.
   * ⚠ **성공 문구를 두지 않는다**(오너 지시 2026-08-28, 위 toast 선언 참조) — 줄의 「공유 중」
   *    표시와 메뉴 항목 이름이 결과를 이미 말한다.
   */
  const runShare = async (id, next) => {
    if (id == null || busy != null) return;
    setBusy(`share:${id}`);
    try {
      const d = await putFolderShares(id, { allTeachers: next }, getAccessToken);
      if (d === AUTH_REDIRECT || !aliveRef.current) return;
      setUnshareId(null);
      // 응답 본문에 갱신된 폴더 항목이 실리는지가 계약에 없다 — 목록을 다시 읽어 맞춘다.
      // all_teachers 는 항목·tree 노드에 실려 오므로(§2-3) 이 한 번으로 줄 표시까지 함께 맞는다.
      await load();
    } catch (err) {
      if (aliveRef.current) fail(describeApiError(err, next == null ? '공유를 취소하지 못했습니다.' : '공유하지 못했습니다.'));
    } finally {
      if (aliveRef.current) setBusy(null);
    }
  };

  /**
   * 줄 하나의 모양. face=false 면 **선택 면을 깔지 않는다** — 폴더 줄은 감싸는 <li> 가 이미
   * 그 면을 깔고 있어서, 버튼에도 깔면 [⋯]·개수 배지가 버튼 밖에 서는 순간 이름 뒤에서 색이
   * 한 단 꺾여 보인다(같은 10% 가 두 겹). '전체'·'없음' 줄은 감싸는 면이 없어 기본값 그대로 쓴다.
   */
  const rowClass = (active, { face = true } = {}) => `flex w-full min-w-0 items-center gap-1.5 rounded-xl px-2 py-1.5 text-left text-xs transition-colors ${
    active ? `${face ? 'bg-primary/10 ' : ''}font-bold text-primary` : 'text-gray-700 hover:bg-gray-50'
  }`;

  const allActive = selectedFolderId == null;

  /**
   * [전체] 옆의 개수 — **서버가 주지 않는 값이라 손에 있는 것으로 만든다.**
   * 봉투가 주는 것은 unfiled_count 하나뿐이고(examPaperApi 의 listFolders), 목록의 총 건수는
   * 시험지 패널 안에 있는 데다 고른 폴더·필터가 걸린 값이라 「전체」가 아니다. 그 값을 얻자고
   * 폴더 없이 목록을 한 번 더 치면 화면을 열 때마다 왕복이 하나 는다.
   *
   * 그래서 **직접 소속(paper_count)을 전부 더하고 무소속을 얹는다.** total_paper_count 를
   * 쓰지 않는 이유: 그것은 자손까지 포함한 수라 최상위만 골라 더해야 하는데, 여기 손에 있는
   * 것은 계층이 아니라 평면 목록(folders)이고 직접 소속은 **어느 폴더에서도 겹치지 않는다.**
   *
   * 하나라도 모르면 전체도 모른다 — 폴더 배지가 '—' 를 쓰는 것과 같은 규칙이다(paper_count 는
   * 계약이 주기로 한 값이지만 없을 때 0 으로 단정하면 화면이 거짓말을 한다 — 위 paperCountOf).
   */
  const allCount = useMemo(() => {
    if (!loaded) return null;
    let sum = unfiledCount;
    for (const f of folders) {
      const c = paperCountOf(f);
      if (c == null) return null;
      sum += c;
    }
    return sum;
  }, [loaded, folders, unfiledCount]);

  /**
   * 끌어다 놓기 — 두 종류다.
   *
   * ① **시험지**(PaperTable 의 행이 소스) → movePapers. 대상은 폴더와 [없음].
   * ② **폴더**(이 트리의 행이 소스) → moveFolder. 대상은 폴더와 [최상위로] 자리.
   *
   * dragover 에서 preventDefault 를 해야 drop 이 일어나는데, 아무 때나 하면 브라우저 밖에서
   * 끌어온 파일·글자에도 폴더가 반응한다. 그래서 **우리 MIME 이 실렸을 때만** 받는다.
   * (그 판정에 types 를 쓰는 이유는 dragover 중에는 getData 가 막혀 있기 때문이다.)
   *
   * [전체]는 폴더가 아니라 필터라 어느 쪽도 받지 않는다. [없음]은 시험지만 받는다 —
   * 폴더에 '무소속'은 없고 '최상위'가 그 자리다.
   */
  const [dropTarget, setDropTarget] = useState(null);   // 시험지 드래그가 떠 있는 대상 id | UNFILED
  const [folderDrop, setFolderDrop] = useState(null);   // 폴더 드래그가 떠 있는 대상 id | ROOT_DROP
  const [dragFolderId, setDragFolderId] = useState(null); // 지금 끌고 있는 폴더(최상위 자리를 그릴지 판정)
  const [moving, setMoving] = useState(false);
  // 판정은 dragover 시점에 **동기로** 필요하다 — state 는 다음 렌더에나 반영돼 늦는다
  const dragFolderRef = useRef(null);

  const isPaperDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(PAPER_DND_MIME);
  const isFolderDrag = (e) => Array.from(e.dataTransfer?.types || []).includes(FOLDER_DND_MIME);

  /** 끌고 있는 폴더 id — 페이로드가 먼저고, 못 읽으면 드래그 시작 때 적어 둔 값 */
  const draggedFolderId = (e) => {
    let payload = null;
    try { payload = JSON.parse(e.dataTransfer.getData(FOLDER_DND_MIME)); } catch (_) { payload = null; }
    return payload?.id ?? dragFolderRef.current ?? null;
  };

  /** 자기 자신이거나 자기 자손인가 — 부모를 타고 올라가며 본다(계약 §2: 둘 다 400) */
  const isSelfOrDescendant = (rootId, id) => {
    if (rootId == null || id == null) return false;
    const seen = new Set();
    let cur = String(id);
    while (cur != null && !seen.has(cur)) {
      if (cur === String(rootId)) return true;
      seen.add(cur);
      const p = treeIndex.get(cur)?.parentId;
      cur = p == null ? null : String(p);
    }
    return false;
  };

  /**
   * 여기에 놓을 수 있는가 — **서버에 묻기 전에 화면이 판정한다**(계약 §2 의 400 두 가지).
   * targetId 가 null 이면 최상위 자리. 이미 트리 안에 있던 가지라 높이가 3 을 넘을 수 없어 늘 받는다.
   */
  const canMoveFolderTo = (srcId, targetId) => {
    if (srcId == null) return false;
    // 공유받은 폴더는 남의 것이다 — 옮기는 것도, 남의 폴더 안으로 넣는 것도 막는다(오너 결정 2026-08-28).
    // 안으로 넣는 것을 막는 이유는 그것이 곧 **그 폴더의 구조를 고치는 일**이라, 「하위 폴더 만들기」를
    // 막는 것과 같은 자리이기 때문이다.
    if (!isMine(srcId)) return false;
    if (targetId == null) return true;
    if (!isMine(targetId)) return false;
    if (isSelfOrDescendant(srcId, targetId)) return false;
    const height = entryOf(srcId)?.height ?? 1;    // 옮기는 가지의 높이(자기를 1로)
    const targetDepth = (entryOf(targetId)?.level ?? 0) + 1; // 받는 폴더의 깊이(최상위=1)
    return targetDepth + height <= MAX_FOLDER_DEPTH;
  };
  // 드래그와 [⋯] 메뉴가 **같은 자**를 쓴다 — 두 벌로 두면 한쪽만 막는 자리가 생긴다
  const canDropFolder = (targetId) => canMoveFolderTo(dragFolderRef.current, targetId);

  /**
   * **시험지**를 여기 놓을 수 있는가 — 폴더 쪽(canMoveFolderTo)과 같은 이유로 자를 한 자리에 둔다.
   * 판정을 dropProps 안에 직접 쓰면 dragover 와 drop 두 자리가 갈려 한쪽만 막는 날이 온다.
   *
   * 축은 **`permission`** 이다(folderOwnership.canPutPapersNode) — `is_mine` 이 아니다.
   * 막을 것은 정확히 view 하나고, is_mine 으로 막으면 매니저가 남의 폴더에 넣던 길까지 닫힌다.
   * 키가 없는 서버(운영 배포분)에서는 전부 참이라 **화면이 한 곳도 바뀌지 않는다.**
   * [없음](UNFILED)은 폴더가 아니라 「폴더에서 빼기」 자리라 늘 받는다 — 등급을 물을 폴더가 없다.
   * 트리에 없는 id 도 참이다(canPutPapersNode 가 모르면 잠그지 않는다).
   */
  const canDropPaperOn = (target) => (target === UNFILED ? true : canPutPapersNode(entryOf(target)?.node));

  const dropProps = (target) => ({
    onDragOver: (e) => {
      if (isPaperDrag(e)) {
        // 거부는 폴더 갈래와 **글자 그대로 같은 방식**이다 — preventDefault 전에 빠져나간다.
        // 그래야 고리(ring)도 안 켜지고 브라우저가 스스로 금지 커서를 그린다. dropEffect='none'
        // 으로 커서만 바꾸는 길을 쓰지 않는 이유는 아래 onDrop 에 적었다.
        if (!canDropPaperOn(target)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTarget !== target) setDropTarget(target);
        return;
      }
      // [없음]은 폴더를 받지 않는다 — 폴더의 '무소속'은 최상위 자리다
      if (!isFolderDrag(e) || target === UNFILED || !canDropFolder(target)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!sameSel(folderDrop, target)) setFolderDrop(target);
    },
    onDragLeave: (e) => {
      // 자식 위로 옮겨 갈 때도 leave 가 난다 — 실제로 이 행을 벗어난 것만 끈다
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDropTarget((cur) => (cur === target ? null : cur));
      setFolderDrop((cur) => (sameSel(cur, target) ? null : cur));
    },
    onDrop: (e) => {
      if (isPaperDrag(e)) {
        e.preventDefault();
        setDropTarget(null);
        // 같은 판정을 여기서 한 번 더 본다 — 위 dragover 가 막으면 브라우저는 drop 을 띄우지 않지만,
        // 끌고 있는 사이 다른 창에서 공유가 끊겨 트리가 다시 읽히면 낡은 상태로 여기까지 온다.
        // **여기서는 문구를 낸다**: 커서만 바꾸는 길(dropEffect='none')로는 drop 이 아예 안 나서
        // 왜 안 들어갔는지가 화면 어디에도 남지 않는다.
        if (!canDropPaperOn(target)) { fail(PAPER_LOCK.title); return; }
        let payload = null;
        try { payload = JSON.parse(e.dataTransfer.getData(PAPER_DND_MIME)); } catch (_) { payload = null; }
        if (payload?.id == null) { fail('무엇을 옮기려는지 알아내지 못했습니다.'); return; }
        dropPaper(payload, target);
        return;
      }
      if (!isFolderDrag(e) || target === UNFILED) return;
      e.preventDefault();
      setFolderDrop(null);
      const src = draggedFolderId(e);
      if (src == null) { fail('무엇을 옮기려는지 알아내지 못했습니다.'); return; }
      dropFolder(src, target);
    },
  });

  /** 최상위로 올리는 자리 — 폴더를 끌고 있을 때만 나타난다 */
  const rootDropProps = {
    onDragOver: (e) => {
      if (!isFolderDrag(e) || !canDropFolder(null)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (folderDrop !== ROOT_DROP) setFolderDrop(ROOT_DROP);
    },
    onDragLeave: (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setFolderDrop((cur) => (cur === ROOT_DROP ? null : cur));
    },
    onDrop: (e) => {
      if (!isFolderDrag(e)) return;
      e.preventDefault();
      setFolderDrop(null);
      const src = draggedFolderId(e);
      if (src == null) { fail('무엇을 옮기려는지 알아내지 못했습니다.'); return; }
      dropFolder(src, null);
    },
  };

  const endFolderDrag = () => {
    dragFolderRef.current = null;
    setDragFolderId(null);
    setFolderDrop(null);
  };

  const dropPaper = async (payload, target) => {
    if (moving) return;
    const toId = target === UNFILED ? null : target;
    // 이미 그 폴더면 서버까지 갈 일이 없다 — 옮겼다고 말하지도 않는다
    const fromId = payload.folderId ?? null;
    // 이미 그 폴더면 아무 일도 하지 않는다 — 화면이 이미 그렇게 그려져 있어 말할 것이 없다
    if (String(fromId) === String(toId)) return;
    setMoving(true);
    setToast(null);
    try {
      const d = await movePapers([Number(payload.id)], toId, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      load();            // 폴더별 개수 배지를 다시 읽는다
      notifyChanged();   // 목록도 다시 읽는다(옮긴 폴더가 지금 보고 있는 폴더면 사라지거나 나타난다)
    } catch (e) {
      // 폴더 이동 실패(dropFolder)와 **같은 토스트 자리**를 쓰는데 문구 만드는 법이 둘이면 그 자체가
      // 결함이다. 서버 사유를 사람 말로 바꾸는 자리는 describeApiError 하나뿐이고, 2026-09-10 에
      // 들어온 403 `out_of_scope`(보기 전용 폴더에 넣으려 함)가 바로 여기로 닿는다.
      fail(describeApiError(e, '시험지를 옮기지 못했습니다.'));
    } finally {
      if (aliveRef.current) setMoving(false);
    }
  };

  /**
   * 폴더 이동 — PATCH parent_id(계약 §2). 낙관적으로 그리지 않는다:
   * 한 번 옮기면 자손의 depth·total_paper_count 가 전부 바뀌어 화면에서 다시 계산할 값이 너무 많다.
   * 실패하면(서버가 400 을 주면) 사람 말로 알리고 서버 상태로 다시 읽어 화면을 되돌린다.
   */
  const dropFolder = async (srcId, targetId) => {
    if (moving) return;
    const src = entryOf(srcId);
    const curParent = src?.parentId ?? null;
    // 이미 그 자리면 아무 일도 하지 않는다(빈 요청은 서버에 판만 쌓는다)
    if (sameSel(curParent, targetId)) return;
    setMoving(true);
    setToast(null);
    try {
      const d = await moveFolder(srcId, targetId, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      await load();
      notifyChanged(); // '하위 폴더 포함'으로 보고 있으면 목록의 모집단이 바뀐다
    } catch (e) {
      fail(describeApiError(e, '폴더를 옮기지 못했습니다.'));
      await load(); // 화면이 옮겨진 척하지 않게 서버 상태로 되돌린다
    } finally {
      if (aliveRef.current) setMoving(false);
    }
  };

  const unfiledActive = sameSel(selectedFolderId, UNFILED);

  /** 새 폴더 이름 입력 — 최상위(머리)와 하위(펼친 폴더 안)가 같은 폼을 쓴다 */
  const renderCreateForm = (parentId, className) => (
    <form onSubmit={submitCreate} className={className}>
      {/* 버튼을 눌러 방금 연 입력이라 초점이 여기 말고 갈 곳이 없다 */}
      <input
        autoFocus
        type="text"
        value={newName}
        onChange={(ev) => setNewName(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === 'Escape') cancelCreate(); }}
        aria-label={parentId == null ? '새 폴더 이름' : '새 하위 폴더 이름'}
        placeholder={parentId == null ? '폴더 이름' : '하위 폴더 이름'}
        maxLength={100}
        className="min-w-0 flex-1 rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
      />
      <Button size="xs" type="submit" loading={busy === 'create'} disabled={newName.trim() === '' || busy != null}>
        만들기
      </Button>
      <Button size="xs" variant="secondary" onClick={cancelCreate} disabled={busy === 'create'}>
        취소
      </Button>
    </form>
  );

  // ── 무리 나눔(FS-02)과 범위(FS-04) ──────────────────────────────────────
  //
  // **값만 가른다** — 줄을 그리는 renderNode 도, 판정에 쓰는 treeIndex 도 그대로다.
  // 가른 결과를 setTree 로 넣지 않는 이유: treeIndex 가 tree 를 그대로 걸어 level·parentId·height
  // 를 만들고 그 위에 깊이 판정·삭제 문구·최상위 띠가 서 있다. tree 의 모양을 바꾸면 셋이 함께 틀어진다.
  // 나눔은 **최상위 줄에만** 건다 — 내 폴더 안에 남의 하위 폴더가 있을 수 있고, 그것까지 가르면
  // 부모·자식이 끊어져 트리로 읽히지 않는다(하위 줄의 소유는 줄의 공유 아이콘이 말한다).
  // 그리는 나무 — 폴더명 오름차순 사본(파일 머리 「순서」). 원본 tree 를 정렬해 넣지 않는 이유: 이름 바꾸기는
  // 원본에 patchNode 로 새 이름만 얹으므로, 원본을 정렬해 두면 다시 읽기 전까지 옛 자리에 머문다.
  // 순서는 treeIndex(level·parentId·height)와 무관해 판정 쪽은 원본을 그대로 쓴다.
  const shownTree = useMemo(() => sortFolderTree(tree), [tree]);
  const groups = useMemo(() => splitTopLevel(shownTree), [shownTree]);
  // 「내 폴더」를 고르면 공유받은 무리를 **통째로** 그리지 않는다 — 제목 줄까지.
  const shown = scope === 'mine' ? { mine: groups.mine, shared: [] } : groups;

  /**
   * 제목 줄을 그리는가 — **무리마다 따로 정한다**(설계 §4 FS-02).
   *
   * 「공유받은 폴더」 제목은 그 무리가 있으면 언제나 선다. 내 폴더가 0개인 계정 —
   * 자기 폴더 없이 공유만 받는 **교사**가 바로 그 화면을 보는데, 제목을 한 값으로 묶어 두면
   * 남의 폴더가 제목 없이 평평하게 서고 「공유받은 것」이라는 말이 줄 끝 아이콘 하나에만 남는다.
   *
   * 「내 폴더」 제목은 가를 상대가 있을 때만 선다. 공유받은 폴더가 0개인 계정에게는 가를 것이
   * 없어 제목이 소음이고, 그 화면은 공유가 생기기 전과 **완전히 같아야** 한다.
   */
  const groupHead = (g) => (g === 'shared'
    ? shown.shared.length > 0
    : shown.mine.length > 0 && shown.shared.length > 0);

  /**
   * 제목 줄이 없으면 접을 손잡이도 없다 — **그 무리는 접힘 값을 무시하고 그린다.**
   * 조건을 무리별로 가른 이상 이 불변식도 무리별로 성립해야 한다. 한 값으로 묶어 두면,
   * 「공유받은 폴더」를 접어 둔 채 공유가 끊겨 무리가 하나가 되는 순간 남은 무리가 통째로
   * 사라지고 다시 펼 길이 없어진다.
   */
  const groupOpen = (g) => !groupHead(g) || openState.groups[g] !== false;

  /**
   * 이 줄이 어느 무리에 속하는가 — 판정은 folderOwnership 이 쥔다(buildGroupIndex · groupOf).
   * 최상위 조상까지 타고 올라가 **그 조상의 소유**로 정하고, 순환·고아도 거기서 막는다.
   * 여기 사본을 두면 같은 폴더가 트리와 시험지 고르기 창에서 다른 무리에 뜨는데 **빌드는 통과한다.**
   * 입력이 tree 가 아니라 평면 목록(folders)인 이유도 같다 — tree 를 안 쓰는 화면과 같은 함수를 쓴다.
   *
   * ⚠ 모르는 id(목록 밖 · 아직 안 읽음)에 groupOf 는 null 이 아니라 'mine' 을 준다.
   *    아래 뒷정리가 그 값을 === g 로 보므로, 「내 폴더」를 접을 때 트리에 없는 id 의 편집 상태까지
   *    함께 닫힌다. 그것은 어차피 그려지지 않는 줄의 상태라 놓아주는 편이 맞다.
   */
  const groupIndex = useMemo(() => buildGroupIndex(folders), [folders]);
  const groupOfRow = (id) => groupOf(groupIndex, id);

  /**
   * 안 그려지게 된 무리의 열린 편집 상태를 놓아준다.
   *
   * 무리 접기와 범위 좁히기는 **결과가 같다** — 그 무리의 줄을 숨기는 것이 아니라 아예 만들지
   * 않는다. 그래서 보이지 않는 자리에 삭제 확인·이름 입력·하위 폴더 입력·[⋯] 메뉴가 상태로만
   * 남고, 다시 펴거나 「전체」로 돌아오는 순간 맥락 없이 되살아난다. 보이지 않는 확인은 무장을
   * 풀어 둔다(확인 바를 펼침 영역 밖에 둔 것은 「확인 중에 접혔다고 없던 일이 되면 안 된다」는
   * 뜻이었지, 줄 자체가 사라진 뒤에도 살려 두라는 뜻이 아니다).
   * 두 자리가 각자 정리하다 갈라진 적이 있어(범위 스위치는 아무것도 안 했고 접기는 메뉴를
   * 안 닫았다) **한 자리에 모은다.** 다른 무리에서 열려 있는 것은 보이는 자리라 그대로 둔다.
   */
  const releaseGroup = (g) => {
    if (confirmId != null && groupOfRow(confirmId) === g) setConfirmId(null);
    // 공유 취소 확인도 삭제 확인과 같은 규칙이다 — 빠뜨리면 접힌 무리 안에 확인 바가 상태로 남아
    // 다시 펴는 순간 맥락 없이 되살아나고, **빌드는 통과한다.**
    if (unshareId != null && groupOfRow(unshareId) === g) setUnshareId(null);
    if (renameId != null && groupOfRow(renameId) === g) setRenameId(null);
    if (creating != null && creating.parentId != null && groupOfRow(creating.parentId) === g) cancelCreate();
    // [⋯] 미니 모달은 body 포털이라 연 줄이 사라져도 화면에 그대로 뜬다. 바깥 클릭 감지가
    // mousedown 이라 키보드로 제목 줄을 누르면(Enter·Space) 걸리지도 않아 스스로 닫히지 않는다.
    if (menuFor != null && groupOfRow(folderIdOf(menuFor.f)) === g) setMenuFor(null);
  };

  /**
   * 무리 접기 — 접으면 그 무리의 줄이 **아예 만들어지지 않는다**(폴더 접기와 같은 규칙).
   *
   * 고른 폴더가 접힌 무리 안에 있어도 **선택은 건드리지 않는다.** 범위 좁히기(changeScope)와
   * 다른 점은 제목 줄이 그 자리에 남아 있다는 것이다 — 어디로 갔는지 화면이 말하고 한 번 눌러
   * 되돌린다. 개별 폴더를 접을 때(toggleOpen)도 자식 줄이 사라지지만 선택을 지우지 않으니,
   * 여기서만 지우면 같은 화면에 「접으면 선택이 풀린다」와 「안 풀린다」 두 규칙이 생긴다.
   */
  const toggleGroup = (g) => {
    const next = openState.groups[g] === false; // 접혀 있었으면 편다
    if (!next) releaseGroup(g);
    commitOpenState({ ...openState, groups: { ...openState.groups, [g]: next } });
  };

  /**
   * 범위 바꾸기(FS-04) — 「내 폴더」로 좁히면 공유받은 무리가 **제목까지** 안 그려진다.
   * 접는 것과 결과가 같으니 뒷정리도 같은 것을 부른다(releaseGroup).
   *
   * 고른 폴더가 그 무리에 있었으면 선택도 「전체」(null)로 되돌린다. 그러지 않으면 트리에는
   * 강조된 줄이 하나도 없는데 오른쪽 시험지 목록은 보이지 않는 폴더로 좁혀진 채라, 두 화면이
   * 서로 다른 말을 한다. 접기와 달리 무리가 흔적 없이 사라져 되돌릴 손잡이도 화면에 없다.
   * 선택은 부모(pages/ExamPapers.js)가 쥐고 있으므로 올리는 길(onSelectFolder)로만 바꾼다.
   * [없음](UNFILED)·[전체](null)는 지도에 없어 groupOf 가 'mine' 을 주므로 그대로 남는다 —
   * 두 줄은 범위와 무관하게 늘 보이는 자리라 맞다.
   */
  const changeScope = (next) => {
    if (next === scope) return;
    if (next === 'mine') {
      releaseGroup('shared');
      if (groupOfRow(selectedFolderId) === 'shared') select(null);
    }
    setScope(next);
  };

  /**
   * 무리 제목 줄 — 누르면 그 무리를 접는다(오너 결정 2026-09-09 2번).
   * 글자 값은 이 파일의 캡션과 **같은 것**을 쓴다(메뉴 머리의 개수 줄·「옮길 곳」 캡션).
   * 여백을 <li> 가 아니라 **버튼**에 거는 이유: 감싸는 <ul> 의 space-y-0.5 는 직계 자식을
   * (0,3,0) 특이도로 잡아 위아래 여백을 2px 로 눌러 버린다. 한 겹 안쪽이면 그 규칙 밖이라
   * mt-2·mb-1 이 그대로 산다. 가로선은 두지 않는다 — 줄이 하나 더 있는 것처럼 보인다.
   * 끌어다 놓기는 붙이지 않는다 — 여기 놓을 수 있는 것이 없고, 대상 자리에 폴더 id 가 아닌
   * 값을 넘기면 그것이 그대로 moveFolder 의 parent_id 로 나간다.
   */
  const renderGroupHead = (g) => {
    const open = groupOpen(g);
    return (
      <li key={`group-${g}`}>
        <button
          type="button"
          onClick={() => toggleGroup(g)}
          aria-expanded={open}
          className="mb-1 mt-2 flex w-full items-center gap-1 px-2 text-left text-[10px] font-medium text-gray-400 transition-colors hover:text-gray-600"
        >
          {open
            ? <ChevronUp aria-hidden="true" className="size-3 shrink-0" />
            : <ChevronDown aria-hidden="true" className="size-3 shrink-0" />}
          <span className="min-w-0 flex-1 truncate">{GROUP_LABEL[g]}</span>
        </button>
      </li>
    );
  };

  /**
   * 폴더 한 줄 — 자기 자식을 안에 그린다(계약 §2 tree).
   * level 은 0부터 세는 **그리는 단계**이고, 계약의 depth 는 1부터다(최상위=1).
   * 들여쓰기는 자식 목록에 한 단씩 pl-3(12px) — 이 저장소 간격 척도 안의 값이다.
   */
  const renderNode = (f, level) => {
    const id = folderIdOf(f);
    const name = folderLabel(f);
    const count = paperCountOf(f);        // 직접 소속
    const total = totalCountOf(f);        // 자손 포함
    const bothCounts = count != null && total != null && total !== count;
    const kids = childrenOf(f);
    const kidCount = childCountOf(f) ?? kids.length;
    const depth = depthOf(f, level);
    // 펼침의 출처는 **세션 집합 하나뿐이다**(FS-01). 여기서 나온 값이 접기 버튼·아이콘·
    // 자식 줄·「고르면 펼치기」로 함께 퍼지므로, 다른 자리에서 f.is_open 을 다시 읽지 않는다.
    const open = id != null && openState.folders.has(String(id));
    const active = sameSel(selectedFolderId, id);
    const renaming = renameId != null && sameSel(renameId, id);
    const confirming = confirmId != null && sameSel(confirmId, id);
    const unsharing = unshareId != null && sameSel(unshareId, id);
    const deleting = busy === `delete:${id}`;
    const unsharingBusy = busy === `share:${id}`;
    const creatingHere = creating != null && creating.parentId != null && sameSel(creating.parentId, id);
    const dragging = dragFolderId != null && sameSel(dragFolderId, id);
    const over = dropTarget === id || (folderDrop != null && folderDrop !== ROOT_DROP && sameSel(folderDrop, id));
    // 상위 폴더 — 삭제 문구가 '상위 폴더로'와 '폴더 없음으로'를 가르는 축이다.
    // 트리 색인이 정본이다(그리는 자리가 곧 계층이라, parent_id 가 비어 와도 옳다).
    const parentId = entryOf(id)?.parentId ?? f?.parent_id ?? null;
    // 공유받은 폴더(남이 만든 것) — 고르고 펼쳐서 볼 수는 있지만 **고칠 수는 없다**(오너 결정 2026-08-28)
    const mine = isMine(id);
    /**
     * 내가 「교사 전원」에게 공유해 **내보낸** 줄인가 — 위 mine 과 짝이 아니라 **다른 축**이다
     * (mine 은 「누가 만들었나」, 이것은 「내가 공유를 걸었나」).
     * `is_shared` 를 쓰지 않는다 — 그 값은 개별 공유까지 합친 것이라(계약 §2-3), 개별 공유가
     * 남아 있으면 [공유 취소] 뒤에도 참이라 표시가 방금 누른 버튼과 어긋난다.
     * 레벨로 한 번 더 거르지 않는다: 계약이 비최상위 PUT 을 400 으로 막아 실제로는 최상위에만
     * 실리고, 여기서 레벨로 거르면 서버가 문을 넓히는 날 화면이 **참을 숨긴다.**
     */
    const shareState = shareStateOf(f);
    const sharedOut = mine && shareState != null;
    /**
     * 삭제 확인 문구 — 계약 §2 로 규칙이 바뀐 자리다. 지우는 것은 폴더뿐이고, 안의 것들은
     * **한 단계 위로 올라간다**. 최상위 폴더만 갈 상위가 없어 시험지가 무소속이 된다.
     * 두 갈래를 한 문장으로 붙이면 하나가 반드시 거짓이 되므로 여기서 갈라 놓는다.
     */
    const deleteNote = (() => {
      const papers = count == null ? '폴더 안의 시험지' : `폴더 안의 시험지 ${count}개`;
      if (parentId == null) {
        return kidCount > 0
          ? `${papers}는 지워지지 않고 폴더 없음으로 가고, 하위 폴더 ${kidCount}개는 최상위로 올라갑니다.`
          : `${papers}는 지워지지 않고 폴더 없음으로 갑니다.`;
      }
      return kidCount > 0
        ? `하위 폴더 ${kidCount}개와 ${papers}는 지워지지 않고 상위 폴더로 이동합니다.`
        : `${papers}는 지워지지 않고 상위 폴더로 이동합니다.`;
    })();

    return (
      <li key={id ?? name} className={active ? 'rounded-xl bg-primary/10' : ''}>
        <div className="flex min-w-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => toggleOpen(id, !open)}
            aria-expanded={open}
            aria-controls={`folder-body-${id}`}
            aria-label={`${name} ${open ? '접기' : '펼치기'}`}
            className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>

          {renaming ? (
            <form onSubmit={(ev) => submitRename(ev, f)} className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1 pr-1">
              {/* [이름 변경]을 눌러 그 자리에 연 입력이라 초점이 따라와야 한다 */}
              <input
                autoFocus
                type="text"
                value={renameName}
                onChange={(ev) => setRenameName(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === 'Escape') setRenameId(null); }}
                aria-label={`${name} 새 이름`}
                maxLength={100}
                className="min-w-0 flex-1 rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none"
              />
              <Button size="xs" type="submit" loading={busy === `rename:${id}`} disabled={renameName.trim() === '' || busy != null}>
                저장
              </Button>
              <Button size="xs" variant="secondary" onClick={() => setRenameId(null)} disabled={busy === `rename:${id}`}>
                취소
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                selectFolder(id);
                /* 고르면 펼친다(오너 지시 2026-08-28). **닫는 것은 왼쪽 [>] 버튼만** 한다 —
                   고르는 것과 접는 것이 같은 자리에 있으면, 목록을 보려고 누른 것이 접기가 된다.
                   자식이 없으면 펼칠 것이 없다 — 서버 왕복은 이제 없지만(FS-01), 빈 상자를 여는
                   상태를 세션에 남길 이유도 없다. */
                if (!open && kids.length > 0) toggleOpen(id, true);
              }}
              aria-current={active ? 'true' : undefined}
              // 편집 중(이름 변경·삭제 확인·만드는 중)에는 끌지 않는다 — 그 화면이 통째로 흔들린다
              draggable={busy == null && id != null && mine}
              onDragStart={(ev) => {
                const payload = JSON.stringify({ id, name, parentId });
                ev.dataTransfer.setData(FOLDER_DND_MIME, payload);
                ev.dataTransfer.setData('text/plain', name); // 폴백(브라우저 밖으로 끌 때)
                ev.dataTransfer.effectAllowed = 'move';
                dragFolderRef.current = id;
                setDragFolderId(id);
              }}
              onDragEnd={endFolderDrag}
              {...dropProps(id)}
              className={`${rowClass(active, { face: false })} ${over ? 'ring-2 ring-primary ring-offset-1' : ''} ${dragging ? 'opacity-40' : ''}`}
            >
              {open
                ? <FolderOpen aria-hidden="true" className="size-3.5 shrink-0" />
                : <Folder aria-hidden="true" className="size-3.5 shrink-0" />}
              <span className="min-w-0 flex-1 truncate" title={name}>{name}</span>
              {/* 「공유 중」 — 작은 사람 아이콘 하나. **숫자는 쓰지 않는다**(오너 규칙 4. 그리고
                  share_count 는 전원 공유에서 0 이라 적으면 그 자체가 거짓이다).

                  자리가 **이름 바로 뒤**인 이유: 오른쪽 끝 두 칸은 뜻이 이미 정해져 있다 —
                  [⋯] 자리는 「누구 것인가」(내 것이면 ⋯, 남의 것이면 Users), 배지는 「몇 개인가」.
                  이름 옆은 「이 폴더가 지금 어떤 상태인가」의 열이라 여기가 맞다. 덤으로 이름 버튼
                  안이면 드롭 대상 고리와 끌기 중 흐림이 이 표시까지 함께 감싼다.

                  ⚠ 그림이 Users 인 것은 **오너 지시**다(「현재 있는 작은 사람 아이콘으로 충분함」).
                  이 파일의 규칙과는 **어긋난다** — 여기서 Users 는 이미 「공유받은 폴더(남의 것)」
                  뜻이고([⋯] 자리·readOnly 안내), 같은 그림이 두 뜻을 맡으면 안 된다고 주석으로
                  두 번 판정해 [공유] 메뉴 항목과 「전체」 필터에서 Users 를 피했다. 지시가 그
                  판정보다 앞서므로 **자리로 가른다**(위 문단 — 열이 다르다). 파일 규칙만 보면
                  Share2 가 더 맞다는 사실을 남겨 둔다 — 다음 사람이 「몰라서 그랬나」로 읽지 않게.

                  설명(title)은 등급별로 갈린다(folderOwnership.sharedOutRowTitle) — 화면이 만드는
                  값은 view 고정이지만 계약은 edit 도 준다. svg 에 걸지 않고 감싸는 요소에 거는 것은
                  이 파일의 다른 title 과 같은 자리다. */}
              {sharedOut && (
                <span className="shrink-0" title={sharedOutRowTitle(shareState)} aria-label={sharedOutRowTitle(shareState)}>
                  <Users aria-hidden="true" className="size-3.5" />
                </span>
              )}
            </button>
          )}

          {/* [⋯] — 손잡이 셋을 여기로 옮겼다. 줄 안에 늘어놓으면 「펼치기」가 자식 보기와
              손잡이 꺼내기 두 가지를 한꺼번에 뜻하게 된다(그 상태가 불편하다는 지적을 받았다).
              이름 변경 중에는 그리지 않는다 — 그 자리는 입력과 [저장]/[취소]가 쓴다. */}
          {!renaming && (
            <button
              type="button"
              onClick={(ev) => {
                const r = ev.currentTarget.getBoundingClientRect();
                // 같은 버튼을 다시 누르면 닫는다
                // 얼려 두는 것은 자리와 **변하지 않는 판정**뿐이다. 펼침은 담지 않는다 —
                // 메뉴가 열려 있는 사이 다른 경로로 목록이 다시 읽히면 낡은 값이 되고,
                // 그 값으로 [하위 폴더]가 부모를 펼칠지 정하면 새 자식이 어디 갔는지 안 보인다.
                setMenuFor((cur) => (cur != null && sameSel(folderIdOf(cur.f), id) ? null : { f, rect: r, depth, mine }));
              }}
              disabled={busy != null}
              aria-haspopup="dialog"
              aria-expanded={menuFor != null && sameSel(folderIdOf(menuFor.f), id)}
              aria-label={`${name} 폴더 메뉴 열기`}
              title={mine ? '하위 폴더 · 이동 · 이름 변경 · 공유 · 삭제' : sharedRowTitle(authorLabel(f))}
              className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            >
              {/* 공유받은 줄은 손잡이가 [⋯] 가 아니라 **공유 아이콘**이다(FS-03) — 메뉴를 열어야
                  알 수 있던 「남의 폴더」를 줄에서 바로 말한다. 버튼이 하는 일도 자리도 크기도
                  그대로라 폭 변화는 0 이고, 만든 사람은 위 설명(title)이 맡는다.
                  자물쇠를 쓰지 않는 이유: 지금 잠그는 것은 화면뿐이고 서버가 막는다는 근거가 없다. */}
              {mine
                ? <MoreHorizontal aria-hidden="true" className="size-3.5" />
                : <Users aria-hidden="true" className="size-3.5" />}
            </button>
          )}

          {/* 개수 배지 — 오너 지시로 [⋯] 와 좌우를 바꿨다(2026-08-28). 그래서 고르는 버튼 **밖**에 선다:
              버튼 안에 두면 [⋯]가 그 앞에 와야 하는데 버튼 안에 버튼을 넣을 수 없다.
              직접 소속 / 자손 포함 — 둘이 같으면 하나만 그린다(같은 수를 두 번 쓰면 소음이다). */}
          {!renaming && (
            <span
              title={bothCounts ? `이 폴더에 ${count}개 · 하위 폴더까지 ${total}개` : undefined}
              className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
            >
              {count == null ? '—' : count}{bothCounts ? ` / ${total}` : ''}
            </span>
          )}
        </div>

        {/* 펼침이 여는 것은 이 폴더의 손잡이와 **자식 줄**이다(계약 §2).
            시험지 목록은 부모 패널이 그리므로 트리 안에 두 번 그리지 않는다.
            접힌 폴더의 자손은 숨기는 것이 아니라 **아예 만들지 않는다** — 깊은 트리에서
            보이지도 않는 수백 줄을 DOM 에 세워 두지 않는다. */}
        {/* 접힘은 hidden 속성이 아니라 클래스로 잰다 — [hidden]{display:none} 은 base 라
            같은 요소의 flex 유틸리티에 진다(접어도 그대로 보인다). */}
        <div id={`folder-body-${id}`} className={open ? 'block' : 'hidden'}>
          {/* 하위 묶음 왼쪽 세로선 — 편집기의 들여쓰기 안내선(오너 2026-09-14, 강의 나무 SyllabusTree 와 같은 얼굴).
              펼침 손잡이(p-1 + 14px = 22px)의 가운데 아래 10~11px 자리, 하위 줄(pl-3)과 1px 떨어진다. 고른 줄이 있어도 옅은 그대로 */}
          {open && kids.length > 0 && (
            <ul className="relative space-y-0.5 pl-3 before:pointer-events-none before:absolute before:inset-y-0 before:left-2.5 before:w-px before:bg-gray-200 before:content-['']">
              {kids.map((k) => renderNode(k, level + 1))}
            </ul>
          )}
        </div>

        {/* 새 하위 폴더 입력 — 확인 바와 같은 이유로 **펼침 영역 밖**에 둔다.
            메뉴는 접힌 폴더에서도 열리는데, 안에 두면 입력이 hidden 안에서 태어나 보이지 않는다.
            (그래서 메뉴의 [하위 폴더]는 접혀 있으면 함께 펼친다 — 만든 뒤 어디 갔는지 보이게.) */}
        {creatingHere && renderCreateForm(
          id,
          'mb-1 ml-8 mr-1 flex flex-wrap items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 p-2',
        )}

        {/* 확인 바 — 지우는 것이 폴더뿐이라는 사실과, **안의 것들이 어디로 가는지**를 결정하는 자리에 같이 남긴다.
            접어도 사라지지 않게 펼침 영역 밖에 둔다(확인 중에 접혔다고 확인이 없던 일이 되면 안 된다). */}
        {confirming && (
          <div className="mb-1 ml-8 mr-1 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
            <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <b>{name}</b> <b>폴더만</b> 지웁니다.
                {' '}{deleteNote}
                {' '}시험지를 지우려면 시험지 목록에서 따로 지워야 합니다.
              </span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Button size="xs" variant="danger" onClick={() => runDelete(f)} loading={deleting} disabled={busy != null}>
                폴더만 삭제
              </Button>
              <Button size="xs" variant="secondary" onClick={() => setConfirmId(null)} disabled={deleting}>
                취소
              </Button>
            </div>
          </div>
        )}

        {/* 공유 취소 확인 바 — 삭제 확인 바와 **같은 슬롯·같은 껍데기**다(새 모달을 만들지 않는다.
            이 파일이 window.confirm 을 쓰지 않는 이유가 파일 머리에 있다: 결정에 필요한 사실이
            화면에 같이 남아 있어야 한다). 여기서 남는 사실은 폴더 이름이다.
            펼침 영역 **밖**인 것도 같은 이유다 — 확인 중에 접혔다고 확인이 없던 일이 되면 안 된다.
            버튼 색은 warning 이다: 되돌릴 수 있는 일이라 danger(빨강)를 쓰지 않는다
            (되돌리기·채점 수정이 이미 쓰는 자리와 같은 등급). 되돌리는 쪽은 '취소' 가 아니라
            SHARE_ALL_TEACHERS.keep 이다 — 한 바에 '취소' 가 둘 서면 어느 쪽이 무엇인지 알 수 없다. */}
        {unsharing && (
          <div className="mb-1 ml-8 mr-1 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
            <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-900">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>{shareCancelNote(name)}</span>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Button size="xs" variant="warning" onClick={() => runShare(id, null)} loading={unsharingBusy} disabled={busy != null}>
                {SHARE_ALL_TEACHERS.cancel}
              </Button>
              <Button size="xs" variant="secondary" onClick={() => setUnshareId(null)} disabled={unsharingBusy}>
                {SHARE_ALL_TEACHERS.keep}
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  };

  const rootDropVisible = dragFolderId != null && (entryOf(dragFolderId)?.parentId ?? null) != null;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-3 shadow-lg">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <h2 className="shrink-0 text-sm font-bold text-gray-800">폴더</h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="폴더 목록 새로고침"
          title="새로고침"
          className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
        >
          <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <Button size="xs" onClick={() => startCreate(null)} disabled={busy != null}>
          <FolderPlus aria-hidden="true" className="mr-0.5 inline size-3" />
          새 폴더
        </Button>
      </div>

      {/* 범위 스위치(FS-04) — 서버에 다시 묻지 않는다. 폴더 목록은 페이지 나눔 없이 전량이 오고
          항목마다 is_mine 이 있어, 「내 폴더」는 **이미 화면이 들고 있는 값**으로 갈린다.
          건수는 넘기지 않는다 — 224px 트리 칸에 들어갈 자리가 없다.
          설명(title)에 한계를 적는다: 이 가름은 최상위 줄에만 걸린다. */}
      <div className="mt-2 shrink-0" title={SCOPE_LIMIT_NOTE}>
        <PaperScopeControls
          mine={scope === 'mine'}
          onMineChange={(v) => changeScope(v ? 'mine' : 'all')}
          options={FOLDER_SCOPE_OPTIONS}
          /* 같은 화면에 이 스위치가 둘 선다(폴더 트리 · 시험지 목록). 눈은 자리로 갈리지만
             스크린리더는 무리 이름만 읽으므로 양쪽이 각자 대상을 말한다(저쪽 기본값은 「시험지 범위」). */
          groupLabel="폴더 범위"
        />
      </div>

      {creating != null && creating.parentId == null && renderCreateForm(
        null,
        'mt-2 flex shrink-0 flex-wrap items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 p-2',
      )}

      {/* 껍데기는 스크롤하지 않는다 — 최상위 드롭 띠를 **자리를 차지하지 않는 덮개**로 띄우려면
          기준이 되는 relative 상자가 스크롤 밖에 있어야 한다(안에 두면 띠가 내용과 함께 흘러간다). */}
      <div className="relative mt-2 min-h-0 flex-1">
      <div className="h-full overflow-y-auto">
        {error && (
          <div className="space-y-2 p-2">
            <p role="alert" className="text-xs text-danger">{error}</p>
            <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
          </div>
        )}

        <ul className="space-y-0.5">
          <li>
            <button type="button" onClick={() => select(null)} aria-current={allActive ? 'true' : undefined} className={rowClass(allActive)}>
              <Layers aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">전체</span>
              {/* [없음]·폴더와 같은 배지다(오너 지시 2026-08-29 「이 우측에도 동일하게」).
                  아직 못 읽었으면 '…', 셀 수 없으면 '—' — 두 상태를 0 으로 뭉개지 않는다. */}
              <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                {loaded ? (allCount == null ? '—' : allCount) : '…'}
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => select(UNFILED)}
              aria-current={unfiledActive ? 'true' : undefined}
              {...dropProps(UNFILED)}
              className={`${rowClass(unfiledActive)} ${dropTarget === UNFILED ? 'ring-2 ring-primary ring-offset-1' : ''}`}
            >
              <Inbox aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">없음</span>
              <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                {loaded ? unfiledCount : '…'}
              </span>
            </button>
          </li>

          {/* 접힌 무리의 자식은 **아예 그리지 않는다** — 숨기는 것이 아니라 만들지 않는다.
              폴더 접기와 같은 규칙이라 이 화면에 규칙이 둘이 되지 않는다. */}
          {groupHead('mine') && renderGroupHead('mine')}
          {groupOpen('mine') && shown.mine.map((f) => renderNode(f, 0))}
          {groupHead('shared') && renderGroupHead('shared')}
          {groupOpen('shared') && shown.shared.map((f) => renderNode(f, 0))}
        </ul>

        {!error && loading && !loaded && (
          <div className="space-y-1 p-1">
            {[0, 1, 2].map((i) => <div key={i} className="h-7 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
          </div>
        )}
        {!error && loaded && folders.length === 0 && (
          <div className="space-y-2 px-2 py-5 text-center">
            <p className="text-xs text-gray-400">폴더가 없습니다 — 시험지를 묶어 두려면 폴더를 만드세요.</p>
            <Button size="xs" variant="secondary" onClick={() => startCreate(null)} disabled={busy != null}>첫 폴더 만들기</Button>
          </div>
        )}
        {/* **걸러서** 빈 경우는 따로 말한다. 위 안내는 `folders`(서버가 준 전체 평면 목록)를 보는데,
            공유받은 폴더가 하나라도 있으면 그 수는 0 이 아니다 — 내 폴더가 0개인 계정(교사)이
            「내 폴더」를 누르면 [전체]·[없음] 두 줄 아래가 백지가 되고 이유를 말하지 않는다.
            돌아가는 길을 함께 둔다: 범위 스위치를 다시 찾아 누르라고 하지 않는다. */}
        {!error && loaded && folders.length > 0 && scope === 'mine' && shown.mine.length === 0 && (
          <div className="space-y-2 px-2 py-5 text-center">
            <p className="text-xs text-gray-400">여기 보일 폴더가 없습니다 — 공유받은 폴더와 그 안의 하위 폴더는 「전체」에서 봅니다.</p>
            <Button size="xs" variant="secondary" onClick={() => changeScope('all')}>전체 보기</Button>
          </div>
        )}
      </div>

      {/* 최상위로 올리는 자리 — 하위 폴더를 끌 때만 뜻이 있다(최상위 폴더는 이미 최상위다).
          ⚠ 예전에는 이 띠가 **목록 안에 흐름으로** 있었다. 그래서 하위 폴더를 집는 순간 띠가 생기며
          목록 전체가 34px 아래로 밀렸고, 커서 밑의 요소가 드래그 도중에 바뀌어 **드롭이 먹지 않았다**.
          최상위 폴더를 끌 때는 띠가 안 생겨 멀쩡했던 것이 그래서다 — 「하위 폴더만 안 된다」의 정체.
          지금은 자리를 차지하지 않는 덮개다. 덮는 것은 [전체]·[없음] 두 줄뿐이고, 그 둘은 원래
          폴더를 받지 않는 자리라 잃는 드롭 대상이 없다. */}
      {rootDropVisible && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10">
          <div
            {...rootDropProps}
            className={`pointer-events-auto rounded-xl border border-dashed px-2 py-1.5 text-center text-[10px] font-medium transition-colors ${
              folderDrop === ROOT_DROP ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 bg-white text-gray-400'
            }`}
          >
            여기에 놓으면 최상위 폴더가 됩니다
          </div>
        </div>
      )}
      </div>

      {/* [⋯] 미니 모달 — 트리 밖(body 포털)에 뜬다. 자리·닫기 규칙은 FolderMenu 가 쥔다. */}
      {menuFor && (() => {
        const mf = menuFor.f;
        const mid = folderIdOf(mf);
        const mName = folderLabel(mf);
        const mCount = paperCountOf(mf);
        const mTotal = totalCountOf(mf);
        const mBoth = mCount != null && mTotal != null && mTotal !== mCount;
        /* **menuFor.f 는 [⋯] 를 연 시점의 스냅샷이다.** 얼려 둔 것은 자리와 「변하지 않는 판정」
           뿐이라는 규칙이 위 [⋯] 주석에 있고, `all_teachers` 는 이 화면이 바로 바꾸는 값이라
           그 규칙 밖이다 — 스냅샷에서 읽으면 [공유] 를 누르고 load() 가 새 노드를 넣어도 메뉴 안
           표시가 낡은 채로 남는다. currentParentId 가 이미 entryOf 로 live 를 읽는 선례를 따른다. */
        const mEntry = entryOf(mid);
        const liveNode = mEntry?.node ?? mf;
        const mParentId = mEntry?.parentId ?? mf?.parent_id ?? null;
        const mShare = shareStateOf(liveNode);
        /* [공유] 항목을 그릴 조건 셋 — 하나라도 어긋나면 **겹으로 들어가는 길 자체가 없다.**
           ① 내 폴더여야 한다(남의 폴더 공유는 내가 정할 수 없다).
           ② 최상위여야 한다. 판정은 currentParentId 와 **같은 식**이다(parentId == null ⟺ 최상위) —
              계약이 비최상위 PUT 을 400 not_root 로 막으므로, 눌러서 알게 하지 않는다
              (canAddChild 가 깊이 3 을 미리 거르는 것과 같은 자리다).
           ③ 서버가 이 필드를 알아야 한다. `undefined` 는 **운영 배포분(구 서버)** 이라는 뜻이고
              `null`(해제됨)과 다르다 — `!= null` 로 보면 둘이 뭉개져, 눌러도 되는 일이 없는
              서버에서 [공유] 가 뜬다(오너 규칙 6). 그 구별은 folderOwnership.shareStateOf 가 쥔다. */
        const canShare = menuFor.mine && mParentId == null && mShare !== undefined;
        /* ②만 어긋난 경우 — 내 폴더이고 서버도 이 기능을 알지만 **하위 폴더라서** 못 거는 자리.
           항목 대신 「위 폴더에서 하면 된다」를 세운다. ③이 어긋난 경우(구 서버)는 여기 들지 않는다 —
           그때는 공유가 화면 어디에도 없어야 한다(오너 규칙 6). */
        const shareSubOnly = menuFor.mine && mParentId != null && mShare !== undefined;
        return (
          <FolderMenu
            /* 대상이 바뀌면 **상자를 새로 세운다.** key 가 없으면 같은 자리에 남아 view 가 살아남는다 —
               마우스는 바깥 mousedown 이 먼저 닫아 주지만 키보드(Tab→Enter)에는 그 신호가 없어,
               내 폴더에서 연 공유·옮길 곳 겹이 **남의 폴더 위에** 그대로 뜬다. */
            key={mid == null ? mName : String(mid)}
            name={mName}
            rect={menuFor.rect}
            countText={`시험지 ${mCount == null ? '—' : mCount}개${mBoth ? ` · 하위 포함 ${mTotal}개` : ''}`}
            // 깊이 3 이면 하위 폴더를 만들 수 없다(계약 §2) — 눌러서 400 을 보는 자리를 두지 않는다
            readOnly={!menuFor.mine}
            /* 만든 사람(authorName)은 **더 넘기지 않는다**(2026-09-10). 공유 겹의 「이 폴더 / 만든 사람 /
               시험지 N개」 묶음을 걷었기 때문이다 — [공유] 는 내 폴더에만 뜨니 만든 사람은 늘 나 자신이고,
               개수는 이 상자 첫 겹 머리가 이미 말한다. 같은 말을 겹을 넘어 두 번 읽히지 않는다. */
            canAddChild={menuFor.mine && menuFor.depth < MAX_FOLDER_DEPTH}
            currentParentId={mParentId}
            canShare={canShare}
            shareOn={mShare != null}
            shareSubOnly={shareSubOnly}
            /* 옮길 곳 — 자기와 자기 자손은 **아예 빼고**(들어갈 수 없는 자리라 목록에 두면 소음이다),
               깊이가 3을 넘게 되는 자리는 남기되 잠근다(왜 못 고르는지 화면이 말해야 한다). */
            moveOptions={(() => {
              const out = [{ id: null, name: '최상위', level: 0, blocked: false }];
              const walk = (nodes, level) => {
                (Array.isArray(nodes) ? nodes : []).forEach((n) => {
                  const nid = folderIdOf(n);
                  if (nid == null || isSelfOrDescendant(mid, nid)) return; // 자기·자손은 그리지 않는다
                  const shared = !isMine(nid);
                  out.push({
                    id: nid,
                    name: folderLabel(n),
                    level: level + 1,
                    blocked: !canMoveFolderTo(mid, nid),
                    // 왜 못 고르는지 화면이 말해야 한다 — 사유가 둘이라 갈라 적는다
                    reason: shared ? SHARED_LOCK.moveReason : '깊이 초과',
                  });
                  walk(childrenOf(n), level + 1);
                });
              };
              // 나무와 같은 순서(폴더명 오름차순) — 이 목록은 나무를 따라 그리는 목록이라 순서가 다르면 찾기 어렵다
              walk(shownTree, 0);
              return out;
            })()}
            onMove={(targetId) => { setMenuFor(null); dropFolder(mid, targetId); }}
            onAddChild={() => {
              setMenuFor(null);
              // 접힌 폴더에 만들면 새 자식이 어디 갔는지 화면에 안 남는다 — 함께 펼친다.
              // 토글이 아니라 **펴기**다: 이미 펼쳐져 있으면 같은 값을 다시 쓸 뿐이라,
              // 메뉴를 연 시점의 낡은 펼침 값을 볼 일이 없다.
              if (mid != null) toggleOpen(mid, true);
              startCreate(mid);
            }}
            onRename={() => { setMenuFor(null); startRename(mf); }}
            /* 공유는 **바로 나간다**(오너 규칙 3) — 되돌릴 수 있는 일이라 확인을 두지 않는다.
               다른 콜백 넷과 같이 setMenuFor(null) 로 시작한다: 메뉴 항목을 누르면 메뉴가 닫힌다. */
            onShare={() => { setMenuFor(null); runShare(mid, 'view'); }}
            /* 취소는 **확인 바를 연다**(규칙 3). 다른 편집 상태를 먼저 끄는 것은 onDelete 와 같은
               줄 모양이다 — 한 줄에 확인 바가 둘 서거나, 이름 입력 옆에 확인이 뜨면 안 된다. */
            onUnshare={() => { setMenuFor(null); setRenameId(null); setCreating(null); setConfirmId(null); setUnshareId(mid); }}
            onDelete={() => { setMenuFor(null); setRenameId(null); setCreating(null); setUnshareId(null); setConfirmId(mid); }}
            onClose={() => setMenuFor(null)}
          />
        );
      })()}

      {/* 오류만 쓰는 자리. 문구가 없을 때도 **요소를 지우지 않는다** — 라이브 영역은 처음부터
          DOM 에 있어야 스크린리더가 변화를 잡는다(나중에 만들어 넣으면 안 읽힌다). */}
      <p
        role="alert"
        aria-live="assertive"
        className={`shrink-0 text-[11px] leading-relaxed ${toast ? 'mt-1 text-danger' : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>
    </div>
  );
}

// 무소속을 고른 상태를 select 값으로 실어 나르는 표식. 빈 문자열을 쓰면 '아직 안 고름'과
// 구별이 안 되는데, 이동에서 그 둘은 완전히 다른 일이다(아래 주석).
const UNFILED_OPTION = '__unfiled__';

/** 셀렉트 안의 들여쓰기 — 공백은 브라우저가 접을 수 있어 줄바꿈 없는 공백(nbsp)을 쓴다 */
const OPTION_INDENT = '\u00A0\u00A0\u00A0';

/**
 * FolderPickerInline — 폴더 하나를 고르는 작은 셀렉트. [이동]·[복사] 대화에서 쓴다.
 *
 * 값은 정수(그 폴더) | null(무소속). ⚠ movePapers 의 folder_id 는 **명시 필수**이고
 * null 이 "폴더에서 빼기"라, 키를 빠뜨리면 400 이다(계약 §2·§3). 그래서 여기 onChange 는
 * 정수 아니면 null 만 올려 보낸다 — undefined 는 절대 올리지 않는다. 아직 아무것도 고르지
 * 않은 상태(자리표시자)는 값이 아니므로 onChange 를 부르지 않는다.
 *
 * 폴더가 계층이 되면서(계약 §2) 목록도 계층 순서로 늘어놓고 깊이만큼 들여쓴다 —
 * `<option>` 은 중첩할 수 없어(optgroup 은 고를 수 없는 머리다) 들여쓰기가 유일한 단서다.
 * **고른 값의 뜻은 그대로다**: 정수 하나 아니면 null.
 */
export function FolderPickerInline({
  folders = [],
  value = null,
  onChange,
  includeUnfiled = true,
  disabled = false,
  ariaLabel = '폴더 선택',
  id,
}) {
  // 부모가 넘겨 주는 것은 평면 items 다 — 여기서 계층으로 접고(정렬 규칙은 examPaperApi 한 곳),
  // 다시 위에서부터 훑어 화면에 늘어놓는다.
  const items = useMemo(() => {
    const flat = [];
    const walk = (nodes, level) => {
      (Array.isArray(nodes) ? nodes : []).forEach((n) => {
        flat.push({ node: n, level });
        walk(n.children, level + 1);
      });
    };
    walk(buildFolderTree(Array.isArray(folders) ? folders : []), 0);
    return flat;
  }, [folders]);
  const known = value != null && items.some((it) => sameSel(folderIdOf(it.node), value));
  // 무소속을 못 고르는 자리에서 value 가 null 이면 아직 안 고른 것이다 — 자리표시자를 보여 준다
  const current = value == null
    ? (includeUnfiled ? UNFILED_OPTION : '')
    : (known ? String(value) : '');

  const handle = (ev) => {
    const v = ev.target.value;
    if (v === '' || typeof onChange !== 'function') return;
    onChange(v === UNFILED_OPTION ? null : Number(v));
  };

  return (
    <select
      id={id}
      value={current}
      onChange={handle}
      disabled={disabled}
      aria-label={ariaLabel}
      className="min-w-0 max-w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none disabled:opacity-40"
    >
      {current === '' && <option value="" disabled>폴더를 고르세요</option>}
      {includeUnfiled && <option value={UNFILED_OPTION}>없음(폴더에서 빼기)</option>}
      {items.map(({ node, level }) => {
        const fid = folderIdOf(node);
        const count = paperCountOf(node);
        /* 볼 수만 있는 폴더는 고를 수 없다 — 이 셀렉트가 쓰이는 [이동]·[복사]는 둘 다
           「시험지를 그 폴더에 넣는」 일이고, 계약이 view 에서 막는다(§2-3).
           사유를 **라벨 꼬리**에 붙이는 이유: `<option>` 에는 title 이 붙지 않아(브라우저가
           그리지 않는다) 설명을 걸 자리가 라벨뿐이다. 그래서 짧은 쪽(PAPER_LOCK.reason)을 쓴다.
           판정은 folderOwnership 의 canPutPapersNode 로 하고 **새 prop 을 만들지 않는다** —
           이 부품은 다른 화면도 쓰고, 넘기게 하면 그 화면마다 판정이 한 벌씩 는다. */
        const blocked = !canPutPapersNode(node);
        return (
          <option key={fid} value={String(fid)} disabled={blocked}>
            {OPTION_INDENT.repeat(level)}{folderLabel(node)}{count == null ? '' : ` (${count})`}
            {blocked ? ` — ${PAPER_LOCK.reason}` : ''}
          </option>
        );
      })}
    </select>
  );
}

export default FolderTree;
